/**
 * EmailVerwaltung — Admin page for email template management, log, SMTP config and testing.
 *
 * Tabs:
 * - Templates: CRUD, editor with variable picker, live preview, test send
 * - Versand-Log: Paginated log table, detail modal, resend
 * - SMTP: Configuration form, connection test
 * - Test: Quick template test with variable editing
 *
 * @page /admin/email
 * @roles admin
 * @i18n email, common
 * @endpoints GET/POST/PUT/DELETE /api/email/templates, /api/email/log, /api/email/smtp
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import useTabParam from '../hooks/useTabParam';
import { Mail, FileText, ScrollText, Server, FlaskConical, Plus, Pencil, Trash2, Eye, Send, RefreshCw, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, X, Copy, Check, Inbox, RotateCcw, Search, CheckCircle, XCircle, AlertTriangle, ExternalLink, Paperclip, Palette, ToggleLeft, ToggleRight, Type, MousePointerClick, ShieldAlert, TestTube2, ListOrdered, Clock, Ban, Zap, Settings, Layers } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Modal } from '../components/ui/Modal';
import { ToggleSwitch } from '../components/ui';

const API = import.meta.env.VITE_API_URL || '';

const TAB_DEFS = [
 { key: 'templates', labelKey: 'email.tab.templates', Icon: FileText },
 { key: 'queue', labelKey: 'email.tab.queue', Icon: ListOrdered },
 { key: 'categories', labelKey: 'email.tab.categories', Icon: Layers },
 { key: 'log', labelKey: 'email.tab.log', Icon: ScrollText },
 { key: 'inbound', labelKey: 'email.tab.inbound', Icon: Inbox },
 { key: 'smtp', labelKey: 'email.tab.smtp', Icon: Server },
 { key: 'test', labelKey: 'email.tab.test', Icon: FlaskConical },
];

const inp = "w-full px-3 py-2 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500";

const TEST_DATA = {
 'empfaenger.name': 'Max Mustermann',
 'empfaenger.email': 'max@example.com',
 'case.name': 'Lieferverzug Arigato #789',
 'case.id': '789',
 'case.url': 'https://app.weship.eu/cases',
 'kunde.name': 'Arigato Distribution GmbH',
 'kunde.ansprechpartner': 'Herr Tanaka',
 'sla.klasse': 'Premium',
 'sla.stunden': '6',
 'sla.deadline': '03.03.2026 13:00',
 'sla.ueberschritten_stunden': '2.5',
 'survey.url': 'https://app.weship.eu/survey/abc123',
 'survey.monat': 'March 2026',
 'zugewiesen.name': 'Lisa Mueller',
 'einladung.url': 'https://app.weship.eu/invite/abc123',
 'einladung.rolle': 'Manager',
 'einladung.eingeladen_von': 'Sam Wagner',
 'einladung.ablauf': '10.03.2026',
};

function replaceVars(template, vars) {
 if (!template) return '';
 return template.replace(/\{\{(\w+(?:\.\w+)*)\}\}/g, (match, path) => {
 return vars[path] != null ? String(vars[path]) : match;
 });
}

/* ── Toast ── */
function Toast({ message, onClose }) {
 useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
 return (
 <div className="fixed bottom-6 right-6 z-50 px-4 py-2.5 rounded-card bg-surface-elevated border border-line shadow-sm text-sm text-content animate-in fade-in slide-in-from-bottom-4">
 {message}
 </div>
 );
}

/* ── buildPreviewHtml — Client-side HTML builder matching backend renderFromConfig ── */
function buildPreviewHtml(config, vars, useBase) {
 if (!config) return '';
 const nl2br = (s) => replaceVars((s || ''), vars).replace(/\n/g, '<br>');
 const btnColor = config.header_color || '#338edc';
 const parts = [];

 if (useBase) {
 if (config.titel) parts.push(`<h2 style="font-size:20px;font-weight:700;color:#1A1A2E;margin:0 0 20px 0;font-family:Arial,Helvetica,sans-serif;">${nl2br(config.titel)}</h2>`);
 if (config.intro) parts.push(`<p style="font-size:15px;line-height:1.6;color:#374151;margin:0 0 16px 0;font-family:Arial,Helvetica,sans-serif;">${nl2br(config.intro)}</p>`);
 if (config.haupttext) parts.push(`<p style="font-size:15px;line-height:1.6;color:#374151;margin:0 0 16px 0;font-family:Arial,Helvetica,sans-serif;">${nl2br(config.haupttext)}</p>`);
 if (config.button_visible !== false && config.button_text && config.button_url) {
 parts.push(`<table cellpadding="0" cellspacing="0" border="0" style="margin:24px 0;"><tr><td style="background-color:${btnColor};border-radius:6px;padding:12px 28px;"><a href="#" style="color:#FFFFFF;text-decoration:none;font-weight:600;font-size:14px;font-family:Arial,Helvetica,sans-serif;">${replaceVars(config.button_text, vars)}</a></td></tr></table>`);
 }
 if (config.footer_visible !== false && config.footer_text) {
 parts.push(`<p style="font-size:13px;line-height:1.6;color:#9CA3AF;margin:24px 0 0 0;font-family:Arial,Helvetica,sans-serif;">${nl2br(config.footer_text)}</p>`);
 }
 // Wrap in simplified cases-base layout
 const inner = parts.join('\n');
 return `<div style="background:#F4F5FA;padding:30px 16px;font-family:Arial,Helvetica,sans-serif;">
 <div style="max-width:560px;margin:0 auto;">
 <div style="text-align:center;margin-bottom:16px;"><span style="font-size:24px;font-weight:700;"><span style="color:#22D3EE;">we</span><span style="color:#374151;">ship</span></span></div>
 <div style="width:50px;height:3px;background:${config.header_color || '#22D3EE'};margin:0 auto 24px;"></div>
 <div style="background:#fff;border-radius:8px;border:1px solid #E5E7EB;padding:36px;">${inner}</div>
 <div style="text-align:center;padding-top:24px;color:#9CA3AF;font-size:11px;">WeShip Fulfillment GmbH · help@weship.eu</div>
 </div>
 </div>`;
 } else {
 if (config.intro) parts.push(`<p style="color:#9ca3af;font-size:14px;line-height:1.6;margin:0 0 12px 0;">${nl2br(config.intro)}</p>`);
 if (config.haupttext) parts.push(`<p style="color:#9ca3af;font-size:14px;line-height:1.6;margin:0 0 12px 0;">${nl2br(config.haupttext)}</p>`);
 if (config.button_visible !== false && config.button_text && config.button_url) {
 parts.push(`<table cellpadding="0" cellspacing="0" style="margin:20px 0;"><tr><td style="background:${btnColor};border-radius:8px;padding:12px 24px;"><a href="#" style="color:#fff;text-decoration:none;font-weight:600;font-size:14px;">${replaceVars(config.button_text, vars)}</a></td></tr></table>`);
 }
 if (config.footer_visible !== false && config.footer_text) {
 parts.push(`<p style="color:#6b7280;font-size:12px;line-height:1.5;margin:16px 0 0 0;">${nl2br(config.footer_text)}</p>`);
 }
 const inner = parts.join('\n');
 const subj = replaceVars(config.titel || '', vars);
 return `<div style="background:#0a0a0f;padding:30px 16px;font-family:system-ui,sans-serif;">
 <div style="max-width:480px;margin:0 auto;background:#111118;border-radius:16px;border:1px solid rgba(255,255,255,0.06);overflow:hidden;">
 <div style="padding:22px 28px 16px;border-bottom:1px solid rgba(255,255,255,0.06);">
 <span style="color:#fff;font-weight:700;font-size:14px;">WeShip</span> <span style="color:#6b7280;font-size:11px;">Performance</span>
 </div>
 <div style="padding:24px 28px;">
 ${subj ? `<div style="color:#fff;font-size:18px;font-weight:700;margin-bottom:16px;">${subj}</div>` : ''}
 ${inner}
 </div>
 <div style="padding:16px 28px;border-top:1px solid rgba(255,255,255,0.06);text-align:center;">
 <span style="color:#4b5563;font-size:11px;">WeShip Performance v2</span>
 </div>
 </div>
 </div>`;
 }
}

/* ── VariablePicker — Reusable dropdown for inserting variables ── */
function VariablePicker({ variables, onInsert, t }) {
 const [open, setOpen] = useState(false);
 const ref = useRef(null);
 useEffect(() => {
 const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
 document.addEventListener('mousedown', handler);
 return () => document.removeEventListener('mousedown', handler);
 }, []);
 return (
 <div className="relative inline-block" ref={ref}>
 <button type="button" onClick={() => setOpen(!open)}
 className="px-2 py-1 rounded text-[10px] font-medium border border-line hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 text-[var(--text-secondary)] hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center gap-1">
 <MousePointerClick className="w-3 h-3" /> {`{{…}}`}
 </button>
 {open && (
 <div className="absolute z-50 top-full left-0 mt-1 bg-surface-elevated border border-line rounded-lg shadow-sm p-2 min-w-[220px] max-h-[240px] overflow-y-auto">
 {variables.map(v => (
 <button key={v} onClick={() => { onInsert(v); setOpen(false); }}
 className="block w-full text-left px-2 py-1 rounded text-[11px] font-mono text-[var(--text-secondary)] hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
 {`{{${v}}}`}
 </button>
 ))}
 </div>
 )}
 </div>
 );
}

/* ── VisualEditor — Full modal for visual template editing ── */
function VisualEditor({ tplId, onClose, onSaved, t, showToast }) {
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [tpl, setTpl] = useState(null);
 const [betreff, setBetreff] = useState('');
 const [config, setConfig] = useState({
 titel: '', intro: '', haupttext: '',
 button_text: '', button_url: '', button_visible: true,
 footer_text: '', footer_visible: true,
 header_color: '#22D3EE', logo_url: '',
 });
 const [previewHtml, setPreviewHtml] = useState('');
 const fieldRefs = {
 betreff: useRef(null),
 titel: useRef(null),
 intro: useRef(null),
 haupttext: useRef(null),
 button_text: useRef(null),
 button_url: useRef(null),
 footer_text: useRef(null),
 };
 const [activeField, setActiveField] = useState('intro');

 const knownVars = [
 'empfaenger.name', 'empfaenger.email',
 'case.name', 'case.id', 'case.url',
 'kunde.name', 'kunde.ansprechpartner',
 'sla.klasse', 'sla.stunden', 'sla.deadline', 'sla.ueberschritten_stunden',
 'survey.url', 'survey.monat',
 'zugewiesen.name',
 'einladung.url', 'einladung.rolle', 'einladung.eingeladen_von', 'einladung.ablauf',
 ];

 // Load template config
 useEffect(() => {
 (async () => {
 try {
 const res = await fetch(`${API}/api/email/templates/${tplId}/config`, { credentials: 'include' });
 const d = await res.json();
 if (d.ok) {
 setTpl(d.template);
 setBetreff(d.template.betreff || '');
 setConfig(c => ({ ...c, ...d.config }));
 }
 } catch { showToast(t('common.error')); }
 setLoading(false);
 })();
 }, [tplId]);

 // Debounced preview
 useEffect(() => {
 const timer = setTimeout(() => {
 const html = buildPreviewHtml(config, TEST_DATA, tpl?.use_base_template);
 setPreviewHtml(html);
 }, 300);
 return () => clearTimeout(timer);
 }, [config, tpl?.use_base_template]);

 const templateVars = (() => {
 if (!tpl) return knownVars;
 let v = tpl.variablen;
 if (typeof v === 'string') { try { v = JSON.parse(v); } catch { v = null; } }
 if (Array.isArray(v) && v.length > 0) return v;
 return knownVars;
 })();

 const insertVariable = (varKey) => {
 const text = `{{${varKey}}}`;
 const field = activeField;
 const ref = fieldRefs[field]?.current;
 if (!ref) return;

 const start = ref.selectionStart || 0;
 const end = ref.selectionEnd || 0;

 if (field === 'betreff') {
 const val = betreff;
 setBetreff(val.slice(0, start) + text + val.slice(end));
 } else {
 const val = config[field] || '';
 setConfig(c => ({ ...c, [field]: val.slice(0, start) + text + val.slice(end) }));
 }
 setTimeout(() => { ref.selectionStart = ref.selectionEnd = start + text.length; ref.focus(); }, 0);
 };

 const handleSave = async () => {
 setSaving(true);
 try {
 const res = await fetch(`${API}/api/email/templates/${tplId}/config`, {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ betreff, config }),
 });
 if (!res.ok) {
 const err = await res.json().catch(() => ({}));
 throw new Error(err.error || 'Error');
 }
 showToast(t('email.visual.saved'));
 onSaved();
 } catch (e) { showToast(e.message); }
 setSaving(false);
 };

 const updateConfig = (key, val) => setConfig(c => ({ ...c, [key]: val }));

 if (loading) {
 return (
 <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
 <div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" />
 </div>
 );
 }

 return (
 <div className="fixed inset-0 z-50 bg-[var(--bg-primary)] flex flex-col">
 {/* Top bar */}
 <div className="flex items-center justify-between px-5 py-3 border-b border-line bg-surface-elevated flex-shrink-0">
 <div className="flex items-center gap-3">
 <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] hover:text-content transition-colors">
 <X className="w-5 h-5" />
 </button>
 <Palette className="w-5 h-5 text-[#338edc]" />
 <span className="text-sm font-semibold text-content">{t('email.visual.title')}</span>
 <span className="text-xs text-[var(--text-tertiary)] font-mono">— {tpl?.name}</span>
 </div>
 <div className="flex items-center gap-2">
 <button onClick={onClose}
 className="px-4 py-2 rounded-lg text-sm text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover transition-colors">
 {t('common.cancel')}
 </button>
 <button onClick={handleSave} disabled={saving}
 className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
 {saving ? '...' : t('common.save')}
 </button>
 </div>
 </div>

 {/* Two-column layout */}
 <div className="flex flex-1 min-h-0">
 {/* Left: Editor */}
 <div className="w-1/2 border-r border-line overflow-y-auto p-5 space-y-5">
 {/* Betreff */}
 <div>
 <div className="flex items-center justify-between mb-1.5">
 <span className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('email.visual.betreff')}</span>
 <VariablePicker variables={templateVars} onInsert={insertVariable} t={t} />
 </div>
 <input ref={fieldRefs.betreff} value={betreff} onChange={e => setBetreff(e.target.value)}
 onFocus={() => setActiveField('betreff')} className={inp} placeholder="Re: {{case.name}}" />
 </div>

 {/* Branding (only for base template) */}
 {tpl?.use_base_template && (
 <div>
 <span className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1.5 block">{t('email.visual.branding')}</span>
 <div className="grid grid-cols-2 gap-3">
 <label className="block">
 <span className="text-[11px] text-[var(--text-secondary)]">{t('email.visual.headerColor')}</span>
 <div className="flex items-center gap-2 mt-1">
 <input type="color" value={config.header_color || '#22D3EE'} onChange={e => updateConfig('header_color', e.target.value)}
 className="w-8 h-8 rounded border border-line cursor-pointer" />
 <input value={config.header_color || '#22D3EE'} onChange={e => updateConfig('header_color', e.target.value)}
 className={`flex-1 ${inp} text-xs font-mono`} />
 </div>
 </label>
 <label className="block">
 <span className="text-[11px] text-[var(--text-secondary)]">{t('email.visual.logoUrl')}</span>
 <input value={config.logo_url || ''} onChange={e => updateConfig('logo_url', e.target.value)}
 className={`mt-1 ${inp} text-xs`} placeholder="https://..." />
 </label>
 </div>
 </div>
 )}

 {/* Content fields */}
 <div>
 <div className="flex items-center justify-between mb-1.5">
 <span className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('email.visual.content')}</span>
 <VariablePicker variables={templateVars} onInsert={insertVariable} t={t} />
 </div>
 <div className="space-y-3">
 <label className="block">
 <span className="text-[11px] text-[var(--text-secondary)]">{t('email.visual.titelField')}</span>
 <input ref={fieldRefs.titel} value={config.titel} onChange={e => updateConfig('titel', e.target.value)}
 onFocus={() => setActiveField('titel')} className={`mt-1 ${inp}`} placeholder="Re: {{original_subject}}" />
 </label>
 <label className="block">
 <span className="text-[11px] text-[var(--text-secondary)]">{t('email.visual.intro')}</span>
 <textarea ref={fieldRefs.intro} value={config.intro} onChange={e => updateConfig('intro', e.target.value)}
 onFocus={() => setActiveField('intro')} rows={3} className={`mt-1 ${inp} resize-y`}
 placeholder="Guten Tag {{empfaenger.name}},&#10;&#10;vielen Dank für Ihre Nachricht." />
 </label>
 <label className="block">
 <span className="text-[11px] text-[var(--text-secondary)]">{t('email.visual.haupttext')}</span>
 <textarea ref={fieldRefs.haupttext} value={config.haupttext} onChange={e => updateConfig('haupttext', e.target.value)}
 onFocus={() => setActiveField('haupttext')} rows={4} className={`mt-1 ${inp} resize-y`}
 placeholder="Unser Team wird sich umgehend um Ihr Anliegen kümmern..." />
 </label>
 </div>
 </div>

 {/* Button section */}
 <div>
 <div className="flex items-center justify-between mb-1.5">
 <span className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('email.visual.button')}</span>
 <button onClick={() => updateConfig('button_visible', !config.button_visible)}
 className="flex items-center gap-1 text-[11px] text-[var(--text-secondary)] hover:text-content transition-colors">
 {config.button_visible ? <ToggleRight className="w-4 h-4 text-[#338edc]" /> : <ToggleLeft className="w-4 h-4" />}
 {t('email.visual.buttonVisible')}
 </button>
 </div>
 {config.button_visible && (
 <div className="grid grid-cols-2 gap-3">
 <label className="block">
 <span className="text-[11px] text-[var(--text-secondary)]">{t('email.visual.buttonText')}</span>
 <input ref={fieldRefs.button_text} value={config.button_text} onChange={e => updateConfig('button_text', e.target.value)}
 onFocus={() => setActiveField('button_text')} className={`mt-1 ${inp}`} placeholder="Case öffnen" />
 </label>
 <label className="block">
 <span className="text-[11px] text-[var(--text-secondary)]">{t('email.visual.buttonUrl')}</span>
 <input ref={fieldRefs.button_url} value={config.button_url} onChange={e => updateConfig('button_url', e.target.value)}
 onFocus={() => setActiveField('button_url')} className={`mt-1 ${inp} text-xs`} placeholder="{{case.url}}" />
 </label>
 </div>
 )}
 </div>

 {/* Footer section */}
 <div>
 <div className="flex items-center justify-between mb-1.5">
 <span className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('email.visual.footer')}</span>
 <button onClick={() => updateConfig('footer_visible', !config.footer_visible)}
 className="flex items-center gap-1 text-[11px] text-[var(--text-secondary)] hover:text-content transition-colors">
 {config.footer_visible ? <ToggleRight className="w-4 h-4 text-[#338edc]" /> : <ToggleLeft className="w-4 h-4" />}
 {t('email.visual.footerVisible')}
 </button>
 </div>
 {config.footer_visible && (
 <textarea ref={fieldRefs.footer_text} value={config.footer_text} onChange={e => updateConfig('footer_text', e.target.value)}
 onFocus={() => setActiveField('footer_text')} rows={2} className={`${inp} resize-y`}
 placeholder="Mit freundlichen Grüßen,&#10;WeShip Support Team" />
 )}
 </div>
 </div>

 {/* Right: Preview */}
 <div className="w-1/2 overflow-y-auto flex flex-col">
 <div className="px-4 py-2.5 border-b border-line bg-surface-elevated flex-shrink-0 flex items-center justify-between">
 <span className="text-[10px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider">{t('email.visual.preview')}</span>
 <span className="text-[10px] text-[var(--text-tertiary)]">{t('email.visual.previewHint')}</span>
 </div>
 <div className="flex-1 p-4">
 <div className="text-[11px] text-[var(--text-secondary)] mb-2">
 {t('email.templates.betreff')}: <span className="text-content font-medium">{replaceVars(betreff, TEST_DATA)}</span>
 </div>
 <div className="rounded-lg overflow-hidden border border-line">
 <iframe
 srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;}</style></head><body>${previewHtml}</body></html>`}
 className="w-full border-0"
 style={{ minHeight: '500px' }}
 sandbox="allow-same-origin"
 title="Email Preview"
 />
 </div>
 </div>
 </div>
 </div>
 </div>
 );
}

/* ═══════════════════════════════════════════════════════════
 Tab 1: Templates
 ═══════════════════════════════════════════════════════════ */
function TemplatesTab({ t, showToast }) {
 const { user } = useAuth();
 const [templates, setTemplates] = useState([]);
 const [loading, setLoading] = useState(true);
 const [filterCategory, setFilterCategory] = useState('');
 const [filterAktiv, setFilterAktiv] = useState('active'); // 'active' | 'inactive' | 'all'
 const [searchQuery, setSearchQuery] = useState('');
 const [editTpl, setEditTpl] = useState(null);
 const [deleteId, setDeleteId] = useState(null);
 const [visualEditId, setVisualEditId] = useState(null);
 const [testTpl, setTestTpl] = useState(null);
 const [testEmail, setTestEmailRaw] = useState(() => localStorage.getItem('weship_test_email') || '');
 const setTestEmail = (v) => { setTestEmailRaw(v); if (v) localStorage.setItem('weship_test_email', v); };
 const [testSending, setTestSending] = useState(false);
 const [readonlyPreview, setReadonlyPreview] = useState(null);

 const load = useCallback(async () => {
 try {
 const d = await fetch(`${API}/api/email/templates`, { credentials: 'include' }).then(r => r.json());
 setTemplates(d.templates || []);
 } catch { /* ignore */ }
 setLoading(false);
 }, []);

 useEffect(() => { load(); }, [load]);

 const handleDelete = async () => {
 if (!deleteId) return;
 try {
 await fetch(`${API}/api/email/templates/${deleteId}`, { method: 'DELETE', credentials: 'include' });
 showToast(t('common.deleted'));
 setDeleteId(null);
 load();
 } catch { showToast(t('common.error')); }
 };

 const categories = [...new Set(templates.map(t => t.category).filter(Boolean))].sort();
 const countActive = templates.filter(t => t.aktiv).length;
 const countInactive = templates.length - countActive;
 const filtered = templates.filter(tpl => {
 if (filterCategory && tpl.category !== filterCategory) return false;
 if (filterAktiv === 'active' && !tpl.aktiv) return false;
 if (filterAktiv === 'inactive' && tpl.aktiv) return false;
 if (searchQuery) {
 const q = searchQuery.toLowerCase();
 if (!(tpl.name || '').toLowerCase().includes(q) &&
 !(tpl.betreff || '').toLowerCase().includes(q) &&
 !(tpl.funktion || '').toLowerCase().includes(q)) return false;
 }
 return true;
 });

 const toggleActive = async (id) => {
 try {
 const res = await fetch(`${API}/api/email/templates/${id}/toggle-active`, { method: 'PATCH', credentials: 'include' });
 const d = await res.json();
 if (d.ok) {
 setTemplates(prev => prev.map(t => t.id === id ? { ...t, aktiv: d.template.aktiv } : t));
 showToast(d.template.aktiv ? t('email.template_activated') : t('email.template_deactivated'));
 }
 } catch { showToast(t('common.error')); }
 };

 if (loading) return <div className="flex items-center justify-center h-48"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>;

 return (
 <div className="space-y-4">
 {/* Filter bar */}
 <div className="flex items-center gap-3 flex-wrap">
 <div className="inline-flex p-[3px] rounded-[10px] border border-[var(--border-default,#E5E7EB)]" style={{ backgroundColor: 'var(--bg-inset, #F8F9FC)' }}>
 {[
 { key: 'active', label: `${t('email.active_templates')} (${countActive})` },
 { key: 'inactive', label: `${t('email.inactive_templates')} (${countInactive})` },
 { key: 'all', label: `${t('email.all_templates')} (${templates.length})` },
 ].map(f => (
 <button key={f.key} onClick={() => setFilterAktiv(f.key)}
 className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${filterAktiv === f.key ? 'bg-white shadow-sm text-[var(--text-primary,#1A1A2E)]' : 'text-[var(--text-secondary,#4B5563)] hover:text-[var(--text-primary,#1A1A2E)]'}`}>
 {f.label}
 </button>
 ))}
 </div>
 <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className={`${inp} w-auto min-w-[180px]`}>
 <option value="">{t('email.alle_kategorien')}</option>
 {categories.map(c => <option key={c} value={c}>{t(`email.category_${c}`) || c}</option>)}
 </select>
 <div className="relative flex-1 max-w-xs">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#9CA3AF' }} />
 <input
 type="text"
 placeholder={t('email.search_templates')}
 value={searchQuery}
 onChange={(e) => setSearchQuery(e.target.value)}
 className={`${inp} pl-9`}
 />
 </div>
 <div className="flex-1" />
 <button onClick={() => setEditTpl({ funktion: '', name: '', betreff: '', body_html: '', variablen_schema: null, is_default: true, aktiv: true })}
 className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors">
 <Plus className="w-3.5 h-3.5" /> {t('email.templates.new')}
 </button>
 </div>

 {/* Template list */}
 <div className="bg-surface-elevated rounded-card border border-line divide-y divide-line">
 {filtered.length === 0 ? (
 <div className="py-12 text-center text-sm text-[var(--text-tertiary)]">{t('email.templates.empty')}</div>
 ) : filtered.map(tpl => (
 <div key={tpl.id} className={`flex items-start gap-4 px-5 py-4 ${!tpl.aktiv ? 'opacity-50' : ''}`}>
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 mb-1">
 {tpl.is_default && (
 <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
 Default
 </span>
 )}
 {tpl.readonly && (
 <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)] dark:bg-[var(--bg-inset,#F8F9FC)] dark:text-[var(--text-tertiary,#6B7280)]">
 {t('email.readonlyBadge')}
 </span>
 )}
 <span className="text-sm font-semibold text-content">{tpl.name}</span>
 <span className="text-xs text-[var(--text-tertiary)] font-mono">{tpl.funktion}</span>
 {tpl.category && (
 <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)] border border-[var(--border-default,#E5E7EB)]">
 {t(`email.category_${tpl.category}`) || tpl.category}
 </span>
 )}
 </div>
 <div className="text-xs text-[var(--text-secondary)] truncate">
 {tpl.betreff ? <>{t('email.templates.betreff')}: {tpl.betreff}</> : null}
 {tpl.readonly && <span className="ml-2 italic text-[var(--text-tertiary)]">{t('email.readonlyNoEdit')}</span>}
 </div>
 </div>
 <div className="flex items-center gap-1 flex-shrink-0">
 {!tpl.readonly && (
 <button onClick={() => toggleActive(tpl.id)}
 className={`text-[10px] font-semibold px-2 py-0.5 rounded cursor-pointer transition-colors ${tpl.aktiv ? 'bg-green-500/15 text-green-500 hover:bg-green-500/25' : 'bg-surface-inset text-content-tertiary hover:bg-[var(--bg-hover)]'}`}
 title={tpl.aktiv ? t('email.template_deactivated') : t('email.template_activated')}>
 {tpl.aktiv ? t('email.active_templates') : t('email.inactive_templates')}
 </button>
 )}
 {tpl.readonly ? (
 <button onClick={async () => {
 try {
 const res = await fetch(`${API}/api/email/templates/${tpl.id}/preview`, {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({}),
 }).then(r => r.json());
 if (res.ok) setReadonlyPreview({ name: tpl.name, html: res.html, betreff: res.betreff });
 } catch { showToast(t('common.error')); }
 }}
 className="p-1.5 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] hover:text-primary-600 dark:hover:text-primary-400 transition-colors" title={t('email.templates.preview')}>
 <Eye className="w-3.5 h-3.5" />
 </button>
 ) : (
 <>
 <button onClick={() => { setTestTpl(tpl); setTestEmail(user?.email || ''); }}
 className="p-1.5 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] hover:text-primary-600 dark:hover:text-primary-400 transition-colors" title="Test senden">
 <Send className="w-3.5 h-3.5" />
 </button>
 <button onClick={() => setVisualEditId(tpl.id)}
 className="p-1.5 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] hover:text-primary-600 dark:hover:text-primary-400 transition-colors" title={t('email.visual.title')}>
 <Palette className="w-3.5 h-3.5" />
 </button>
 <button onClick={() => setEditTpl({ ...tpl })}
 className="p-1.5 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] hover:text-content transition-colors" title={t('email.templates.edit')}>
 <Pencil className="w-3.5 h-3.5" />
 </button>
 <button onClick={() => setDeleteId(tpl.id)}
 className="p-1.5 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] hover:text-red-500 transition-colors" title={t('email.templates.delete')}>
 <Trash2 className="w-3.5 h-3.5" />
 </button>
 </>
 )}
 </div>
 </div>
 ))}
 </div>

 {/* Editor Modal */}
 {editTpl && <TemplateEditor tpl={editTpl} onClose={() => setEditTpl(null)} onSaved={() => { setEditTpl(null); load(); }} t={t} showToast={showToast} />}

 {/* Delete confirmation */}
 <Modal open={!!deleteId} onClose={() => setDeleteId(null)} title={t('email.templates.delete')} size="sm"
 footer={<>
 <button onClick={() => setDeleteId(null)} className="px-4 py-2 rounded-lg text-sm text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover transition-colors">{t('common.cancel')}</button>
 <button onClick={handleDelete} className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors">{t('email.templates.delete')}</button>
 </>}>
 <p className="text-sm text-content">{t('email.templates.deleteConfirm')}</p>
 </Modal>

 {/* Test-Send Modal */}
 <Modal open={!!testTpl} onClose={() => setTestTpl(null)} title={`Test senden: ${testTpl?.name || ''}`} size="sm"
 footer={<>
 <button onClick={() => setTestTpl(null)} className="px-4 py-2 rounded-lg text-sm text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover transition-colors">{t('common.cancel')}</button>
 <button disabled={testSending || !testEmail} onClick={async () => {
 setTestSending(true);
 try {
 const res = await fetch(`${API}/api/email/templates/${testTpl.id}/test`, {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ to: testEmail, variablen: TEST_DATA }),
 }).then(r => r.json());
 if (res.ok) {
 showToast(`Test-Mail gesendet an ${testEmail}`);
 setTestTpl(null);
 } else {
 showToast(res.error || 'Fehler beim Senden');
 }
 } catch { showToast('Fehler beim Senden'); }
 setTestSending(false);
 }} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
 {testSending ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
 {testSending ? 'Sende...' : 'Senden'}
 </button>
 </>}>
 <div className="space-y-3">
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Empfänger E-Mail</label>
 <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
 className={inp} placeholder="email@example.com" />
 </div>
 <p className="text-xs text-[var(--text-tertiary)]">
 Variablen werden mit Beispieldaten ersetzt. Betreff wird mit [TEST] markiert.
 </p>
 </div>
 </Modal>

 {/* Read-only Preview Modal */}
 <Modal open={!!readonlyPreview} onClose={() => setReadonlyPreview(null)} title={readonlyPreview?.name || ''} size="lg"
 footer={<button onClick={() => setReadonlyPreview(null)} className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors">{t('common.close')}</button>}>
 <div className="space-y-4">
 <div className="rounded-xl border border-[var(--border-default,#E5E7EB)] p-4" style={{ backgroundColor: 'var(--bg-inset, #F8F9FC)' }}>
 <div className="flex items-start gap-3">
 <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0 text-[var(--text-secondary,#4B5563)]" />
 <p className="text-xs text-[var(--text-secondary,#4B5563)] leading-relaxed">{t('email.readonlyInfo')}</p>
 </div>
 </div>
 {readonlyPreview?.betreff && (
 <div className="text-xs text-[var(--text-secondary)]"><strong>{t('email.templates.betreff')}:</strong> {readonlyPreview.betreff}</div>
 )}
 {readonlyPreview?.html && (
 <iframe
 srcDoc={readonlyPreview.html}
 className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg"
 style={{ height: 500, background: '#fff' }}
 sandbox=""
 title="Preview"
 />
 )}
 </div>
 </Modal>

 {/* Visual Editor */}
 {visualEditId && (
 <VisualEditor tplId={visualEditId} onClose={() => setVisualEditId(null)}
 onSaved={() => { setVisualEditId(null); load(); }} t={t} showToast={showToast} />
 )}
 </div>
 );
}

/* ── Template Editor Modal ── */
function TemplateEditor({ tpl, onClose, onSaved, t, showToast }) {
 const [form, setForm] = useState({ ...tpl });
 const [saving, setSaving] = useState(false);
 const [testing, setTesting] = useState(false);
 const [showPreview, setShowPreview] = useState(false);
 const bodyRef = useRef(null);
 const betreffRef = useRef(null);
 const [insertTarget, setInsertTarget] = useState('body'); // 'body' | 'betreff'

 const knownVars = [
 'empfaenger.name', 'empfaenger.email',
 'case.name', 'case.id', 'case.url',
 'kunde.name', 'kunde.ansprechpartner',
 'sla.klasse', 'sla.stunden', 'sla.deadline', 'sla.ueberschritten_stunden',
 'survey.url', 'survey.monat',
 'zugewiesen.name',
 'einladung.url', 'einladung.rolle', 'einladung.eingeladen_von', 'einladung.ablauf',
 ];

 const insertVariable = (varKey) => {
 const text = `{{${varKey}}}`;
 if (insertTarget === 'betreff' && betreffRef.current) {
 const el = betreffRef.current;
 const start = el.selectionStart;
 const end = el.selectionEnd;
 const val = form.betreff || '';
 setForm(p => ({ ...p, betreff: val.slice(0, start) + text + val.slice(end) }));
 setTimeout(() => { el.selectionStart = el.selectionEnd = start + text.length; el.focus(); }, 0);
 } else if (bodyRef.current) {
 const el = bodyRef.current;
 const start = el.selectionStart;
 const end = el.selectionEnd;
 const val = form.body_html || '';
 setForm(p => ({ ...p, body_html: val.slice(0, start) + text + val.slice(end) }));
 setTimeout(() => { el.selectionStart = el.selectionEnd = start + text.length; el.focus(); }, 0);
 }
 };

 const handleSave = async () => {
 if (!form.funktion || !form.name || !form.betreff || !form.body_html) {
 showToast(t('email.editor.missingFields'));
 return;
 }
 setSaving(true);
 try {
 const isNew = !form.id;
 const url = isNew ? `${API}/api/email/templates` : `${API}/api/email/templates/${form.id}`;
 const res = await fetch(url, {
 method: isNew ? 'POST' : 'PUT',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify(form),
 });
 if (!res.ok) {
 const err = await res.json().catch(() => ({}));
 throw new Error(err.error || 'Error');
 }
 showToast(t('common.saved'));
 onSaved();
 } catch (e) { showToast(e.message); }
 setSaving(false);
 };

 const handleTest = async () => {
 if (!form.id) { showToast(t('email.editor.saveFirst')); return; }
 setTesting(true);
 try {
 const res = await fetch(`${API}/api/email/templates/${form.id}/test`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ variablen: TEST_DATA }),
 });
 const d = await res.json();
 showToast(d.ok ? t('email.templates.testSent') : (d.reason || t('email.templates.testFailed')));
 } catch { showToast(t('email.templates.testFailed')); }
 setTesting(false);
 };

 const previewBetreff = replaceVars(form.betreff, TEST_DATA);
 const previewBody = replaceVars(form.body_html, TEST_DATA);

 return (
 <Modal open={true} onClose={onClose} title={form.id ? t('email.templates.edit') : t('email.templates.new')} size="xl"
 footer={<>
 <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover transition-colors">{t('common.cancel')}</button>
 {form.id && (
 <button onClick={handleTest} disabled={testing}
 className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-line hover:bg-surface-hover transition-colors text-content disabled:opacity-50">
 <Send className="w-3.5 h-3.5" /> {testing ? '...' : t('email.templates.test')}
 </button>
 )}
 <button onClick={handleSave} disabled={saving}
 className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
 {saving ? '...' : t('common.save')}
 </button>
 </>}>
 <div className="space-y-4">
 {/* Top row: name + function + toggles */}
 <div className="grid grid-cols-2 gap-3">
 <label className="block">
 <span className="text-xs font-medium text-[var(--text-secondary)]">{t('email.templates.name')}</span>
 <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className={`mt-1 ${inp}`} placeholder="SLA Eskalation" />
 </label>
 <label className="block">
 <span className="text-xs font-medium text-[var(--text-secondary)]">{t('email.templates.funktion')}</span>
 <input value={form.funktion} onChange={e => setForm(p => ({ ...p, funktion: e.target.value }))} className={`mt-1 ${inp}`} placeholder="sla_eskalation" disabled={!!form.id} />
 </label>
 </div>
 <div className="flex items-center gap-4">
 <label className="flex items-center gap-2 text-sm cursor-pointer">
 <input type="checkbox" checked={form.aktiv !== false} onChange={e => setForm(p => ({ ...p, aktiv: e.target.checked }))}
 className="w-4 h-4 rounded border-line text-[#338edc] focus:ring-primary-500/30" />
 <span className="text-content">{t('email.templates.aktiv')}</span>
 </label>
 <label className="flex items-center gap-2 text-sm cursor-pointer">
 <input type="checkbox" checked={form.is_default !== false} onChange={e => setForm(p => ({ ...p, is_default: e.target.checked }))}
 className="w-4 h-4 rounded border-line text-[#338edc] focus:ring-primary-500/30" />
 <span className="text-content">{t('email.templates.isDefault')}</span>
 </label>
 </div>

 {/* Subject */}
 <label className="block">
 <span className="text-xs font-medium text-[var(--text-secondary)]">{t('email.templates.betreff')}</span>
 <input ref={betreffRef} value={form.betreff} onChange={e => setForm(p => ({ ...p, betreff: e.target.value }))}
 onFocus={() => setInsertTarget('betreff')} className={`mt-1 ${inp}`} placeholder="{{case.name}} — SLA Violation" />
 </label>

 {/* Variable Picker */}
 <div>
 <span className="text-xs font-medium text-[var(--text-secondary)]">{t('email.editor.variables')}</span>
 <p className="text-[10px] text-[var(--text-tertiary)] mb-1.5">{t('email.editor.variablesHint')}</p>
 <div className="flex flex-wrap gap-1.5">
 {knownVars.map(v => (
 <button key={v} onClick={() => insertVariable(v)}
 className="px-2 py-1 rounded text-[11px] font-mono border border-line hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 text-[var(--text-secondary)] hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
 {`{{${v}}}`}
 </button>
 ))}
 </div>
 </div>

 {/* Body HTML + Preview toggle */}
 <div>
 <div className="flex items-center justify-between mb-1">
 <span className="text-xs font-medium text-[var(--text-secondary)]">{t('email.templates.body')}</span>
 <button onClick={() => setShowPreview(!showPreview)}
 className="flex items-center gap-1 text-[11px] font-medium text-[var(--text-secondary)] hover:text-primary-600 transition-colors">
 <Eye className="w-3 h-3" /> {showPreview ? t('email.editor.hidePreview') : t('email.editor.showPreview')}
 </button>
 </div>

 <div className={showPreview ? 'grid grid-cols-2 gap-3' : ''}>
 <textarea ref={bodyRef} value={form.body_html || ''} onChange={e => setForm(p => ({ ...p, body_html: e.target.value }))}
 onFocus={() => setInsertTarget('body')} rows={12}
 className={`${inp} font-mono text-xs resize-y`} placeholder='<p style="color:#9ca3af;">Hello {{empfaenger.name}},</p>' />

 {showPreview && (
 <div className="border border-line rounded-lg overflow-hidden">
 <div className="px-3 py-1.5 border-b border-line bg-surface-inset text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wider">
 {t('email.editor.preview')}
 </div>
 <div className="p-3 text-xs">
 <div className="text-[10px] text-[var(--text-tertiary)] mb-2">{t('email.templates.betreff')}: <span className="text-content">{previewBetreff}</span></div>
 <div className="bg-[#0a0a0f] rounded-lg p-3 overflow-auto max-h-[250px]" dangerouslySetInnerHTML={{ __html: previewBody }} />
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 </Modal>
 );
}

/* ═══════════════════════════════════════════════════════════
 Tab 2: Versand-Log
 ═══════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════
 QueueTab — E-Mail Warteschlange Dashboard
 ═══════════════════════════════════════════════════════════ */

const QUEUE_STATUS_COLORS = {
 held: 'bg-amber-500/15 text-amber-500',
 pending: 'bg-blue-500/15 text-blue-500',
 sending: 'bg-cyan-500/15 text-cyan-500',
 sent: 'bg-green-500/15 text-green-500',
 failed: 'bg-red-500/15 text-red-500',
 cancelled: 'bg-[rgba(107,114,128,0.15)] text-[var(--text-secondary,#4B5563)]',
};

// ═══════════════════════════════════════════════════════════════════
// CATEGORIES TAB — per-category queue settings
// ═══════════════════════════════════════════════════════════════════

const KATEGORIE_ORDER = ['cases', 'portal', 'adressklaerung', 'flex', 'forecast', 'survey', 'sales', 'flowai', 'system', 'sonstige'];

function CategoriesTab({ t, language, showToast }) {
 const [rows, setRows] = useState([]);
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [changes, setChanges] = useState({});
 const [collapsed, setCollapsed] = useState({});
 const [showResetConfirm, setShowResetConfirm] = useState(false);

 const loadCategories = useCallback(async () => {
 setLoading(true);
 try {
 const d = await fetch(`${API}/api/email/queue/categories`, { credentials: 'include' }).then(r => r.json());
 setRows(d.categories || []);
 setChanges({});
 } catch { /* ignore */ }
 setLoading(false);
 }, []);

 useEffect(() => { loadCategories(); }, [loadCategories]);

 // Group rows by kategorie
 const grouped = {};
 for (const r of rows) {
 if (!grouped[r.kategorie]) grouped[r.kategorie] = [];
 grouped[r.kategorie].push(r);
 }

 // Get effective value for a row (local change or original)
 const getVal = (row, field) => {
 if (changes[row.id] && changes[row.id][field] !== undefined) return changes[row.id][field];
 return row[field];
 };

 const setChange = (id, field, val) => {
 setChanges(prev => ({
 ...prev,
 [id]: { ...prev[id], [field]: val },
 }));
 };

 // Check if a function row is overriding its category default
 const isOverridden = (row) => {
 if (!row.funktion) return false;
 const catDefault = rows.find(r => r.kategorie === row.kategorie && !r.funktion);
 if (!catDefault) return false;
 const rowQueueAktiv = getVal(row, 'queue_aktiv');
 const defaultQueueAktiv = getVal(catDefault, 'queue_aktiv');
 return rowQueueAktiv !== defaultQueueAktiv;
 };

 const hasChanges = Object.keys(changes).length > 0;

 const handleSave = async () => {
 setSaving(true);
 try {
 const updates = Object.entries(changes).map(([id, ch]) => {
 const orig = rows.find(r => r.id === Number(id));
 return {
 id: Number(id),
 queue_aktiv: ch.queue_aktiv !== undefined ? ch.queue_aktiv : orig.queue_aktiv,
 haltezeit_minuten: ch.haltezeit_minuten !== undefined ? ch.haltezeit_minuten : orig.haltezeit_minuten,
 };
 });
 await fetch(`${API}/api/email/queue/categories`, {
 method: 'PUT',
 credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ updates }),
 });
 showToast(t('email.categoriesSaved'));
 await loadCategories();
 } catch { /* ignore */ }
 setSaving(false);
 };

 const handleReset = async () => {
 setShowResetConfirm(false);
 setSaving(true);
 try {
 await fetch(`${API}/api/email/queue/categories/reset`, {
 method: 'POST', credentials: 'include',
 });
 showToast(t('email.categoriesSaved'));
 await loadCategories();
 } catch { /* ignore */ }
 setSaving(false);
 };

 const toggleCollapse = (kat) => {
 setCollapsed(prev => ({ ...prev, [kat]: !prev[kat] }));
 };

 if (loading) {
 return <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-[var(--text-secondary)]" /></div>;
 }

 return (
 <div className="border border-line rounded-2xl bg-white dark:bg-surface-elevated">
 {/* Header */}
 <div className="flex items-center justify-between px-5 py-4 border-b border-line">
 <div>
 <h3 className="text-sm font-semibold text-content">{t('email.categoriesTitle')}</h3>
 <p className="text-xs text-[var(--text-secondary)] mt-0.5">{t('email.categoriesDesc')}</p>
 </div>
 <button
 onClick={() => setShowResetConfirm(true)}
 className="text-xs text-[var(--text-secondary)] hover:text-content border border-line rounded-lg px-3 py-1.5 transition-colors"
 >
 {t('email.resetCategories')}
 </button>
 </div>

 {/* Table header */}
 <div className="grid grid-cols-[1fr_140px_100px] gap-2 px-5 py-2.5 border-b border-line text-[11px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
 <span>{t('email.tab.categories')}</span>
 <span className="text-center">{t('email.modeQueue')}</span>
 <span className="text-center">{t('email.holdTime')}</span>
 </div>

 {/* Category groups */}
 <div className="divide-y divide-line">
 {KATEGORIE_ORDER.filter(k => grouped[k]).map(kat => {
 const items = grouped[kat];
 const catDefault = items.find(r => !r.funktion);
 const functions = items.filter(r => r.funktion);
 const isCollapsed = collapsed[kat];
 const immCount = items.filter(r => !getVal(r, 'queue_aktiv')).length;
 const catLabel = t(`email.cat.${kat}`);

 return (
 <div key={kat}>
 {/* Category header */}
 <button
 onClick={() => toggleCollapse(kat)}
 className="w-full flex items-center justify-between px-5 py-3 hover:bg-[var(--bg-inset)] transition-colors"
 >
 <div className="flex items-center gap-2">
 {isCollapsed ? <ChevronRight className="w-3.5 h-3.5 text-[var(--text-secondary)]" /> : <ChevronDown className="w-3.5 h-3.5 text-[var(--text-secondary)]" />}
 <span className="text-sm font-semibold text-content">{catLabel}</span>
 <span className="text-[11px] text-[var(--text-secondary)]">
 {t('email.categorySummary').replace('{{count}}', String(items.length)).replace('{{immediate}}', String(immCount))}
 </span>
 </div>
 </button>

 {/* Function rows */}
 {!isCollapsed && (
 <div>
 {/* Default row */}
 {catDefault && (
 <CategoryRow
 row={catDefault}
 isDefault
 t={t}
 language={language}
 queueAktiv={getVal(catDefault, 'queue_aktiv')}
 haltezeit={getVal(catDefault, 'haltezeit_minuten')}
 overridden={false}
 changed={!!changes[catDefault.id]}
 onChangeQueue={(v) => setChange(catDefault.id, 'queue_aktiv', v)}
 onChangeHalt={(v) => setChange(catDefault.id, 'haltezeit_minuten', v)}
 />
 )}
 {functions.map(fn => (
 <CategoryRow
 key={fn.id}
 row={fn}
 isDefault={false}
 t={t}
 language={language}
 queueAktiv={getVal(fn, 'queue_aktiv')}
 haltezeit={getVal(fn, 'haltezeit_minuten')}
 overridden={isOverridden(fn)}
 changed={!!changes[fn.id]}
 onChangeQueue={(v) => setChange(fn.id, 'queue_aktiv', v)}
 onChangeHalt={(v) => setChange(fn.id, 'haltezeit_minuten', v)}
 />
 ))}
 </div>
 )}
 </div>
 );
 })}
 </div>

 {/* Save button */}
 {hasChanges && (
 <div className="flex justify-end px-5 py-3 border-t border-line">
 <button
 onClick={handleSave}
 disabled={saving}
 className="px-5 py-2 text-sm font-semibold text-white bg-[#338edc] rounded-lg hover:bg-[#2b7bc4] disabled:opacity-50 transition-colors"
 >
 {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : t('common.save')}
 </button>
 </div>
 )}

 {/* Reset confirm dialog */}
 {showResetConfirm && (
 <Modal open onClose={() => setShowResetConfirm(false)}>
 <div className="p-6 max-w-sm">
 <h3 className="text-sm font-semibold text-content mb-2">{t('email.resetCategories')}</h3>
 <p className="text-sm text-[var(--text-secondary)] mb-4">{t('email.resetCategoriesConfirm')}</p>
 <div className="flex justify-end gap-2">
 <button onClick={() => setShowResetConfirm(false)} className="px-4 py-2 text-sm border border-line rounded-lg text-content hover:bg-surface-inset transition-colors">{t('common.cancel')}</button>
 <button onClick={handleReset} className="px-4 py-2 text-sm font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors">{t('email.resetCategories')}</button>
 </div>
 </div>
 </Modal>
 )}
 </div>
 );
}

function CategoryRow({ row, isDefault, t, language, queueAktiv, haltezeit, overridden, changed, onChangeQueue, onChangeHalt }) {
 const label = language === 'de'
 ? (row.label_de || row.funktion || row.kategorie)
 : (row.label_en || row.funktion || row.kategorie);

 return (
 <div className={`grid grid-cols-[1fr_140px_100px] gap-2 items-center px-5 py-2 ${
 changed ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
 } ${!isDefault ? 'pl-10' : 'pl-7'}`}>
 {/* Label */}
 <div className="flex items-center gap-2">
 <span className={`text-[13px] ${isDefault ? 'font-medium text-content' : 'text-[var(--text-secondary)]'}`}>
 {label}
 </span>
 {overridden && (
 <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-medium">
 {t('email.overridden')}
 </span>
 )}
 </div>

 {/* Radio: Sofort / Queue */}
 <div className="flex items-center justify-center gap-1">
 <button
 onClick={() => onChangeQueue(false)}
 className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-colors ${
 !queueAktiv
 ? 'bg-[#338edc] text-white'
 : 'bg-[var(--bg-inset,#F8F9FC)] dark:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)] hover:text-content'
 }`}
 >
 {t('email.modeImmediate')}
 </button>
 <button
 onClick={() => onChangeQueue(true)}
 className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-colors ${
 queueAktiv
 ? 'bg-[#338edc] text-white'
 : 'bg-[var(--bg-inset,#F8F9FC)] dark:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)] hover:text-content'
 }`}
 >
 {t('email.modeQueue')}
 </button>
 </div>

 {/* Hold time input */}
 <div className="flex items-center justify-center gap-1">
 <input
 type="number"
 min={0}
 max={1440}
 value={haltezeit}
 onChange={(e) => onChangeHalt(Math.max(0, parseInt(e.target.value) || 0))}
 disabled={!queueAktiv}
 className={`w-14 px-2 py-1 text-xs text-center rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-1 focus:ring-primary-500/30 ${
 !queueAktiv ? 'opacity-40 cursor-not-allowed' : ''
 }`}
 />
 <span className="text-[11px] text-[var(--text-secondary)]">{t('email.holdTimeMin')}</span>
 </div>
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════════
// QUEUE TAB
// ═══════════════════════════════════════════════════════════════════

function QueueTab({ t, showToast }) {
 const { user } = useAuth();
 const [queue, setQueue] = useState([]);
 const [total, setTotal] = useState(0);
 const [stats, setStats] = useState(null);
 const [loading, setLoading] = useState(true);
 const [page, setPage] = useState(0);
 const [filterStatus, setFilterStatus] = useState('');
 const [filterChannel, setFilterChannel] = useState('');
 const [searchTerm, setSearchTerm] = useState('');
 const [selected, setSelected] = useState(new Set());
 const [detailEntry, setDetailEntry] = useState(null);
 const [previewModal, setPreviewModal] = useState(null); // { entry, html, view: 'rendered'|'source', loading }
 const [showConfig, setShowConfig] = useState(false);
 const [queueConfig, setQueueConfig] = useState(null);
 const [actionLoading, setActionLoading] = useState(false);
 const PAGE_SIZE = 30;
 const autoRefreshRef = useRef(null);

 const loadQueue = useCallback(async () => {
 setLoading(true);
 try {
 const params = new URLSearchParams({ limit: PAGE_SIZE, offset: page * PAGE_SIZE });
 if (filterStatus) params.set('status', filterStatus);
 if (filterChannel) params.set('channel', filterChannel);
 if (searchTerm) params.set('search', searchTerm);
 const d = await fetch(`${API}/api/email/queue?${params}`, { credentials: 'include' }).then(r => r.json());
 setQueue(d.queue || []);
 setTotal(d.total || 0);
 } catch { /* ignore */ }
 setLoading(false);
 }, [page, filterStatus, filterChannel, searchTerm]);

 const loadStats = useCallback(async () => {
 try {
 const d = await fetch(`${API}/api/email/queue/stats`, { credentials: 'include' }).then(r => r.json());
 setStats(d.stats);
 } catch { /* ignore */ }
 }, []);

 useEffect(() => { loadQueue(); }, [loadQueue]);
 useEffect(() => { loadStats(); }, [loadStats]);

 // Auto-refresh every 30s
 useEffect(() => {
 autoRefreshRef.current = setInterval(() => { loadQueue(); loadStats(); }, 30000);
 return () => clearInterval(autoRefreshRef.current);
 }, [loadQueue, loadStats]);

 const handleSendNow = async (id) => {
 setActionLoading(true);
 try {
 const d = await fetch(`${API}/api/email/queue/${id}/send-now`, { method: 'POST', credentials: 'include' }).then(r => r.json());
 showToast(d.ok ? t('email.queue.sentSuccess') : (d.error || t('common.error')));
 loadQueue(); loadStats();
 } catch { showToast(t('common.error')); }
 setActionLoading(false);
 };

 const handleCancel = async (id) => {
 setActionLoading(true);
 try {
 await fetch(`${API}/api/email/queue/${id}/cancel`, { method: 'POST', credentials: 'include' });
 showToast(t('email.queue.cancelSuccess'));
 loadQueue(); loadStats();
 } catch { showToast(t('common.error')); }
 setActionLoading(false);
 };

 const handleBulkSend = async () => {
 if (selected.size === 0) return;
 setActionLoading(true);
 try {
 const d = await fetch(`${API}/api/email/queue/bulk-send`, {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ ids: [...selected] }),
 }).then(r => r.json());
 showToast(t('email.queue.bulkSendResult', { sent: d.sent, failed: d.failed }));
 setSelected(new Set());
 loadQueue(); loadStats();
 } catch { showToast(t('common.error')); }
 setActionLoading(false);
 };

 const handleBulkCancel = async () => {
 if (selected.size === 0) return;
 setActionLoading(true);
 try {
 const d = await fetch(`${API}/api/email/queue/bulk-cancel`, {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ ids: [...selected] }),
 }).then(r => r.json());
 showToast(t('email.queue.bulkCancelResult', { count: d.cancelled }));
 setSelected(new Set());
 loadQueue(); loadStats();
 } catch { showToast(t('common.error')); }
 setActionLoading(false);
 };

 const openPreview = async (entry) => {
 setPreviewModal({ entry, html: '', view: 'rendered', loading: true });
 try {
 const d = await fetch(`${API}/api/email/queue/${entry.id}/preview`, { credentials: 'include' }).then(r => r.json());
 setPreviewModal({ entry, html: d.html || '', view: 'rendered', loading: false });
 } catch {
 setPreviewModal({ entry, html: '', view: 'rendered', loading: false });
 }
 };

 const openPreviewInNewTab = (id) => {
 if (!id) return;
 window.open(`${API}/api/email/queue/${id}/preview.html`, '_blank', 'noopener,noreferrer');
 };

 // #1258 — Detail-Endpoint nachladen (liefert CC/BCC/Anhänge; Listen-Row hat die nicht).
 const loadQueueDetail = async (id) => {
 try {
 const d = await fetch(`${API}/api/email/queue/${id}`, { credentials: 'include' }).then(r => r.json());
 if (d.ok && d.entry) setDetailEntry(prev => (prev && prev.id === id ? { ...prev, ...d.entry } : prev));
 } catch { /* Listen-Row-Fallback bleibt sichtbar */ }
 };

 const loadConfig = async () => {
 try {
 const d = await fetch(`${API}/api/email/queue/config`, { credentials: 'include' }).then(r => r.json());
 setQueueConfig(d.config);
 } catch {}
 };

 const saveConfig = async () => {
 if (!queueConfig) return;
 try {
 await fetch(`${API}/api/email/queue/config`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(queueConfig),
 });
 showToast(t('email.queue.configSaved'));
 } catch { showToast(t('common.error')); }
 };

 const toggleSelect = (id) => {
 setSelected(prev => {
 const next = new Set(prev);
 next.has(id) ? next.delete(id) : next.add(id);
 return next;
 });
 };

 const toggleSelectAll = () => {
 const actionable = queue.filter(q => q.status === 'held' || q.status === 'pending');
 if (selected.size === actionable.length && actionable.length > 0) {
 setSelected(new Set());
 } else {
 setSelected(new Set(actionable.map(q => q.id)));
 }
 };

 const totalPages = Math.ceil(total / PAGE_SIZE);

 const fmtDate = (d) => d ? new Date(d).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–';
 // #1258 — Byte-Größe menschenlesbar (z.B. „377 KB", „1.2 MB")
 const fmtBytes = (n) => {
 if (!Number.isFinite(n) || n < 0) return '';
 if (n < 1024) return `${n} B`;
 if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
 return `${(n / (1024 * 1024)).toFixed(1)} MB`;
 };

 return (
 <div className="space-y-4">
 {/* Stats bar */}
 {stats && (
 <div className="flex items-center gap-6 px-5 py-3 bg-surface-elevated rounded-card border border-line text-sm flex-wrap">
 <div><span className="text-[var(--text-secondary)]">{t('email.queue.held')}:</span> <span className="font-semibold text-content">{stats.held}</span></div>
 <div><span className="text-[var(--text-secondary)]">{t('email.queue.pending')}:</span> <span className="font-semibold text-content">{stats.pending}</span></div>
 <div><span className="text-[var(--text-secondary)]">{t('email.queue.readyToSend')}:</span> <span className="font-semibold text-amber-500">{stats.ready_to_send}</span></div>
 <div><span className="text-[var(--text-secondary)]">{t('email.queue.sent')}:</span> <span className="font-semibold text-green-500">{stats.sent}</span></div>
 <div><span className="text-[var(--text-secondary)]">{t('email.queue.failed')}:</span> <span className="font-semibold text-red-500">{stats.failed}</span></div>
 <div><span className="text-[var(--text-secondary)]">{t('email.queue.cancelled')}:</span> <span className="font-semibold text-content">{stats.cancelled}</span></div>
 </div>
 )}

 {/* Filters + Actions */}
 <div className="flex items-center gap-3 flex-wrap">
 <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(0); }} className={`${inp} w-auto min-w-[140px]`}>
 <option value="">{t('email.queue.allStatus')}</option>
 <option value="held">{t('email.queue.held')}</option>
 <option value="pending">{t('email.queue.pending')}</option>
 <option value="sending">{t('email.queue.sending')}</option>
 <option value="sent">{t('email.queue.sent')}</option>
 <option value="failed">{t('email.queue.failed')}</option>
 <option value="cancelled">{t('email.queue.cancelled')}</option>
 </select>
 <select value={filterChannel} onChange={e => { setFilterChannel(e.target.value); setPage(0); }} className={`${inp} w-auto min-w-[120px]`}>
 <option value="">{t('email.queue.allChannels')}</option>
 <option value="global">Global</option>
 <option value="cases">Cases</option>
 <option value="sales">Sales</option>
 </select>
 <div className="relative">
 <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
 <input
 type="text" value={searchTerm}
 onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
 placeholder={t('email.queue.search')}
 className={`${inp} pl-9 w-[200px]`}
 />
 </div>
 <div className="flex-1" />
 {selected.size > 0 && (
 <div className="flex items-center gap-2">
 <span className="text-xs text-[var(--text-secondary)]">{selected.size} {t('email.queue.selected')}</span>
 <button onClick={handleBulkSend} disabled={actionLoading}
 className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
 <Zap className="w-3 h-3" /> {t('email.queue.sendNow')}
 </button>
 <button onClick={handleBulkCancel} disabled={actionLoading}
 className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors disabled:opacity-50">
 <Ban className="w-3 h-3" /> {t('email.queue.cancel')}
 </button>
 </div>
 )}
 <button onClick={() => { setShowConfig(true); loadConfig(); }}
 className="p-2 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] hover:text-content transition-colors" title={t('email.queue.settings')}>
 <Settings className="w-4 h-4" />
 </button>
 <button onClick={() => { loadQueue(); loadStats(); }}
 className="p-2 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] hover:text-content transition-colors">
 <RefreshCw className="w-4 h-4" />
 </button>
 </div>

 {/* Queue table */}
 <div className="bg-surface-elevated rounded-card border border-line overflow-hidden">
 {loading ? (
 <div className="flex items-center justify-center h-48"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>
 ) : queue.length === 0 ? (
 <div className="py-12 text-center text-sm text-[var(--text-tertiary)]">{t('email.queue.empty')}</div>
 ) : (
 <table className="w-full text-sm">
 <thead>
 <tr className="border-b border-line">
 <th className="w-10 px-3 py-2.5">
 <input type="checkbox" onChange={toggleSelectAll}
 checked={selected.size > 0 && selected.size === queue.filter(q => q.status === 'held' || q.status === 'pending').length}
 className="rounded border-line" />
 </th>
 <th className="text-left px-3 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('email.queue.date')}</th>
 <th className="text-left px-3 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('email.queue.recipient')}</th>
 <th className="text-left px-3 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('email.queue.subject')}</th>
 <th className="text-left px-3 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('email.queue.channel')}</th>
 <th className="text-left px-3 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('email.queue.status')}</th>
 <th className="text-left px-3 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('email.queue.holdUntil')}</th>
 <th className="w-20" />
 </tr>
 </thead>
 <tbody className="divide-y divide-line/50">
 {queue.map(entry => (
 <tr key={entry.id}
 className="hover:bg-surface-hover cursor-pointer transition-colors"
 onClick={() => { setDetailEntry(entry); setPreviewHtml(null); loadQueueDetail(entry.id); }}>
 <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
 {(entry.status === 'held' || entry.status === 'pending') && (
 <input type="checkbox" checked={selected.has(entry.id)} onChange={() => toggleSelect(entry.id)} className="rounded border-line" />
 )}
 </td>
 <td className="px-3 py-2.5 text-xs text-[var(--text-secondary)] whitespace-nowrap">{fmtDate(entry.created_at)}</td>
 <td className="px-3 py-2.5 text-content truncate max-w-[180px]">{entry.to_address}</td>
 <td className="px-3 py-2.5 max-w-[260px]">
 <div className="text-content truncate">{entry.subject}</div>
 {/* #1258 — CC / BCC / Anhänge direkt in der Liste (Anhänge als Download-Link) */}
 {(entry.cc?.length > 0 || entry.bcc?.length > 0 || entry.attachments?.length > 0) && (
 <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-1">
 {entry.cc?.length > 0 && (
 <span className="text-[10px] text-[var(--text-tertiary)] truncate max-w-[240px]" title={`CC: ${entry.cc.join(', ')}`}>
 {t('email.queue.cc')}: {entry.cc.join(', ')}
 </span>
 )}
 {entry.bcc?.length > 0 && (
 <span className="text-[10px] text-[var(--text-tertiary)] truncate max-w-[240px]" title={`BCC: ${entry.bcc.join(', ')}`}>
 {t('email.queue.bcc')}: {entry.bcc.join(', ')}
 </span>
 )}
 {entry.attachments?.map((att) => (
 <a key={att.idx} href={`${API}/api/email/queue/${entry.id}/attachment/${att.idx}`}
 onClick={e => e.stopPropagation()} download
 title={`${att.filename}${att.size != null ? ' · ' + fmtBytes(att.size) : ''}`}
 className="inline-flex items-center gap-1 text-[10px] text-[#338edc] hover:underline max-w-[200px]">
 <Paperclip className="w-3 h-3 shrink-0" />
 <span className="truncate">{att.filename}</span>
 </a>
 ))}
 </div>
 )}
 </td>
 <td className="px-3 py-2.5 text-xs text-[var(--text-secondary)] font-mono">{entry.channel}</td>
 <td className="px-3 py-2.5">
 <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${QUEUE_STATUS_COLORS[entry.status] || 'bg-[rgba(107,114,128,0.15)] text-[var(--text-secondary,#4B5563)]'}`}>
 {t(`email.queue.${entry.status}`) || entry.status}
 </span>
 </td>
 <td className="px-3 py-2.5 text-xs text-[var(--text-secondary)] whitespace-nowrap">{fmtDate(entry.hold_until)}</td>
 <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
 {(entry.status === 'held' || entry.status === 'pending') && (
 <div className="flex items-center gap-1">
 <button onClick={() => handleSendNow(entry.id)} disabled={actionLoading}
 className="p-1 rounded hover:bg-surface-hover text-[#338edc] transition-colors disabled:opacity-50" title={t('email.queue.sendNow')}>
 <Zap className="w-3.5 h-3.5" />
 </button>
 <button onClick={() => handleCancel(entry.id)} disabled={actionLoading}
 className="p-1 rounded hover:bg-surface-hover text-red-500 transition-colors disabled:opacity-50" title={t('email.queue.cancel')}>
 <Ban className="w-3.5 h-3.5" />
 </button>
 </div>
 )}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 )}

 {/* Pagination */}
 {totalPages > 1 && (
 <div className="flex items-center justify-between px-5 py-3 border-t border-line">
 <span className="text-xs text-[var(--text-secondary)]">
 {t('email.pagination.showing', { from: page * PAGE_SIZE + 1, to: Math.min((page + 1) * PAGE_SIZE, total), total })}
 </span>
 <div className="flex items-center gap-1">
 <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
 className="p-1.5 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] disabled:opacity-30 transition-colors">
 <ChevronLeft className="w-4 h-4" />
 </button>
 <span className="text-xs text-content px-2">{page + 1} / {totalPages}</span>
 <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
 className="p-1.5 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] disabled:opacity-30 transition-colors">
 <ChevronRight className="w-4 h-4" />
 </button>
 </div>
 </div>
 )}
 </div>

 {/* Detail Modal */}
 <Modal open={!!detailEntry} onClose={() => setDetailEntry(null)} title={t('email.queue.detail')} size="lg"
 footer={detailEntry && (detailEntry.status === 'held' || detailEntry.status === 'pending') ? (
 <div className="flex items-center gap-2">
 <button onClick={() => { handleSendNow(detailEntry.id); setDetailEntry(null); }}
 className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors">
 <Zap className="w-3.5 h-3.5" /> {t('email.queue.sendNow')}
 </button>
 <button onClick={() => { handleCancel(detailEntry.id); setDetailEntry(null); }}
 className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors">
 <Ban className="w-3.5 h-3.5" /> {t('email.queue.cancel')}
 </button>
 </div>
 ) : null}>
 {detailEntry && (
 <div className="space-y-3">
 <div className="grid grid-cols-2 gap-3 text-sm">
 <div><span className="text-[var(--text-secondary)]">{t('email.queue.recipient')}:</span> <span className="text-content ml-1">{detailEntry.to_address}</span></div>
 <div><span className="text-[var(--text-secondary)]">{t('email.queue.channel')}:</span> <span className="text-content ml-1 font-mono">{detailEntry.channel}</span></div>
 <div><span className="text-[var(--text-secondary)]">{t('email.queue.status')}:</span>
 <span className={`ml-1 text-[10px] font-semibold px-2 py-0.5 rounded ${QUEUE_STATUS_COLORS[detailEntry.status] || ''}`}>
 {t(`email.queue.${detailEntry.status}`) || detailEntry.status}
 </span>
 </div>
 <div><span className="text-[var(--text-secondary)]">{t('email.queue.priority')}:</span> <span className="text-content ml-1">{detailEntry.priority}</span></div>
 <div><span className="text-[var(--text-secondary)]">{t('email.queue.date')}:</span> <span className="text-content ml-1">{fmtDate(detailEntry.created_at)}</span></div>
 <div><span className="text-[var(--text-secondary)]">{t('email.queue.holdUntil')}:</span> <span className="text-content ml-1">{fmtDate(detailEntry.hold_until)}</span></div>
 {detailEntry.retry_count > 0 && (
 <div><span className="text-[var(--text-secondary)]">{t('email.queue.retries')}:</span> <span className="text-content ml-1">{detailEntry.retry_count}/{detailEntry.max_retries}</span></div>
 )}
 {detailEntry.sent_at && (
 <div><span className="text-[var(--text-secondary)]">{t('email.queue.sentAt')}:</span> <span className="text-content ml-1">{fmtDate(detailEntry.sent_at)}</span></div>
 )}
 </div>
 <div className="text-sm"><span className="text-[var(--text-secondary)]">{t('email.queue.subject')}:</span> <span className="text-content ml-1 font-medium">{detailEntry.subject}</span></div>
 {/* #1258 — CC / BCC / Anhänge nur wenn vorhanden */}
 {detailEntry.cc?.length > 0 && (
 <div className="text-sm"><span className="text-[var(--text-secondary)]">{t('email.queue.cc')}:</span> <span className="text-content ml-1 break-all">{detailEntry.cc.join(', ')}</span></div>
 )}
 {detailEntry.bcc?.length > 0 && (
 <div className="text-sm"><span className="text-[var(--text-secondary)]">{t('email.queue.bcc')}:</span> <span className="text-content ml-1 break-all">{detailEntry.bcc.join(', ')}</span></div>
 )}
 {detailEntry.attachments?.length > 0 && (
 <div className="text-sm">
 <span className="text-[var(--text-secondary)]">{t('email.queue.attachments')}:</span>
 <ul className="mt-1.5 space-y-1">
 {detailEntry.attachments.map((att, i) => (
 <li key={i}>
 <a href={`${API}/api/email/queue/${detailEntry.id}/attachment/${att.idx ?? i}`} download
 className="inline-flex items-center gap-2 text-[#338edc] hover:underline">
 <Paperclip className="w-3.5 h-3.5 shrink-0" />
 <span className="break-all">{att.filename}</span>
 {att.size != null && <span className="text-[var(--text-tertiary)] whitespace-nowrap">· {fmtBytes(att.size)}</span>}
 </a>
 </li>
 ))}
 </ul>
 </div>
 )}
 {detailEntry.last_error && (
 <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">
 {t('email.queue.error')}: {detailEntry.last_error}
 </div>
 )}
 {/* HTML Preview — opens separate large modal */}
 <div>
 <button onClick={() => openPreview(detailEntry)}
 className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors">
 <Eye className="w-3.5 h-3.5" /> {t('email.queue.showPreview')}
 </button>
 </div>
 </div>
 )}
 </Modal>

 {/* HTML Preview Modal (overlays Detail Modal) */}
 <Modal
 open={!!previewModal}
 onClose={() => setPreviewModal(null)}
 title={t('email.queue.previewTitle')}
 size="full"
 footer={previewModal && (
 <div className="flex items-center justify-between w-full">
 <div className="inline-flex items-center gap-1 p-[3px] rounded-[10px] bg-[var(--bg-inset,#F8F9FC)] border border-line">
 <button
 onClick={() => setPreviewModal(p => p ? { ...p, view: 'rendered' } : p)}
 className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-xs font-semibold transition-colors ${previewModal.view === 'rendered' ? 'bg-white text-content shadow-sm' : 'text-[var(--text-secondary)] hover:text-content'}`}>
 <Eye className="w-3.5 h-3.5" /> {t('email.queue.viewRendered')}
 </button>
 <button
 onClick={() => setPreviewModal(p => p ? { ...p, view: 'source' } : p)}
 className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-xs font-semibold transition-colors ${previewModal.view === 'source' ? 'bg-white text-content shadow-sm' : 'text-[var(--text-secondary)] hover:text-content'}`}>
 <FileText className="w-3.5 h-3.5" /> {t('email.queue.viewSource')}
 </button>
 </div>
 <div className="flex items-center gap-2">
 <button
 onClick={() => openPreviewInNewTab(previewModal.entry?.id)}
 className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-surface-hover text-content hover:bg-surface-elevated border border-line transition-colors">
 <ExternalLink className="w-3.5 h-3.5" /> {t('email.queue.openInNewTab')}
 </button>
 <button
 onClick={() => setPreviewModal(null)}
 className="px-4 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors">
 {t('common.close')}
 </button>
 </div>
 </div>
 )}>
 {previewModal && (
 <div className="flex flex-col h-full">
 {/* Mail-Header (kompakt) */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-xs px-1 pb-3 mb-3 border-b border-line">
 <div className="flex items-baseline gap-2">
 <span className="text-[var(--text-secondary)] shrink-0">{t('email.queue.recipient')}:</span>
 <span className="text-content font-medium truncate">{previewModal.entry?.to_address}</span>
 </div>
 <div className="flex items-baseline gap-2">
 <span className="text-[var(--text-secondary)] shrink-0">{t('email.queue.channel')}:</span>
 <span className="text-content font-mono">{previewModal.entry?.channel}</span>
 </div>
 <div className="md:col-span-2 flex items-baseline gap-2">
 <span className="text-[var(--text-secondary)] shrink-0">{t('email.queue.subject')}:</span>
 <span className="text-content font-medium truncate">{previewModal.entry?.subject}</span>
 </div>
 <div className="flex items-baseline gap-2">
 <span className="text-[var(--text-secondary)] shrink-0">{t('email.queue.date')}:</span>
 <span className="text-content">{fmtDate(previewModal.entry?.created_at)}</span>
 </div>
 <div className="flex items-baseline gap-2">
 <span className="text-[var(--text-secondary)] shrink-0">{t('email.queue.status')}:</span>
 <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${QUEUE_STATUS_COLORS[previewModal.entry?.status] || ''}`}>
 {t(`email.queue.${previewModal.entry?.status}`) || previewModal.entry?.status}
 </span>
 </div>
 </div>
 {/* Body — Mail-Client-Layout: zentrierter 680px-Container auf grauem Background */}
 {/* #1048 RE-RE-OPEN: definite height (h-[60vh] min-h-[480px]) statt flex-1+min-h.
  Modal max-h-[85vh] propagiert keine definite height; iframe als replaced-element
  fällt sonst auf UA-Default 150px zurück → Body+Footer abgeschnitten. */}
 <div className="h-[60vh] min-h-[480px] rounded-lg border border-line overflow-hidden bg-[#f3f4f6]">
 {previewModal.loading ? (
 <div className="h-full flex items-center justify-center text-sm text-[var(--text-secondary)]">{t('common.loading')}…</div>
 ) : previewModal.view === 'source' ? (
 <pre className="w-full h-full overflow-auto p-4 text-[11px] font-mono text-content bg-white whitespace-pre-wrap break-all">{previewModal.html || ''}</pre>
 ) : previewModal.entry?.id ? (
 <iframe
 src={`${API}/api/email/queue/${previewModal.entry.id}/preview.html`}
 title="Email HTML Preview"
 className="w-full h-full border-0 bg-[#f3f4f6]"
 />
 ) : (
 <div className="h-full flex items-center justify-center text-sm text-[var(--text-secondary)]">(kein HTML-Inhalt)</div>
 )}
 </div>
 </div>
 )}
 </Modal>

 {/* Config Modal */}
 <Modal open={showConfig} onClose={() => setShowConfig(false)} title={t('email.queue.settingsTitle')}
 footer={
 <button onClick={() => { saveConfig(); setShowConfig(false); }}
 className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors">
 <Check className="w-3.5 h-3.5" /> {t('common.save')}
 </button>
 }>
 {queueConfig && (
 <div className="space-y-4">
 <div className="flex items-center justify-between">
 <div>
 <div className="text-sm font-medium text-content">{t('email.queue.enabled')}</div>
 <div className="text-xs text-[var(--text-secondary)]">{t('email.queue.enabledDesc')}</div>
 </div>
 <ToggleSwitch checked={queueConfig.enabled} onChange={() => setQueueConfig(c => ({ ...c, enabled: !c.enabled }))} />
 </div>
 <div>
 <label className="text-sm font-medium text-content">{t('email.queue.holdMinutes')}</label>
 <input type="number" value={queueConfig.hold_minutes} min={0} max={1440}
 onChange={e => setQueueConfig(c => ({ ...c, hold_minutes: Number(e.target.value) }))}
 className={`${inp} mt-1`} />
 <div className="text-xs text-[var(--text-secondary)] mt-1">{t('email.queue.holdMinutesDesc')}</div>
 </div>
 <div>
 <label className="text-sm font-medium text-content">{t('email.queue.batchSize')}</label>
 <input type="number" value={queueConfig.batch_size} min={1} max={100}
 onChange={e => setQueueConfig(c => ({ ...c, batch_size: Number(e.target.value) }))}
 className={`${inp} mt-1`} />
 </div>
 <div>
 <label className="text-sm font-medium text-content">{t('email.queue.retryDelays')}</label>
 <input type="text" value={queueConfig.retry_delays}
 onChange={e => setQueueConfig(c => ({ ...c, retry_delays: e.target.value }))}
 className={`${inp} mt-1`} />
 <div className="text-xs text-[var(--text-secondary)] mt-1">{t('email.queue.retryDelaysDesc')}</div>
 </div>
 </div>
 )}
 </Modal>
 </div>
 );
}

function LogTab({ t, showToast }) {
 const [log, setLog] = useState([]);
 const [total, setTotal] = useState(0);
 const [stats, setStats] = useState(null);
 const [loading, setLoading] = useState(true);
 const [page, setPage] = useState(0);
 const [filterStatus, setFilterStatus] = useState('');
 const [filterFn, setFilterFn] = useState('');
 const [detailEntry, setDetailEntry] = useState(null);
 const [historyLoading, setHistoryLoading] = useState(false);
 const [history, setHistory] = useState(null);
 const [resending, setResending] = useState(null);
 const PAGE_SIZE = 30;

 const loadLog = useCallback(async () => {
 setLoading(true);
 try {
 const params = new URLSearchParams({ limit: PAGE_SIZE, offset: page * PAGE_SIZE, grouped: '1' });
 if (filterStatus) params.set('status', filterStatus);
 if (filterFn) params.set('funktion', filterFn);
 const d = await fetch(`${API}/api/email/log?${params}`, { credentials: 'include' }).then(r => r.json());
 setLog(d.log || []);
 setTotal(d.total || 0);
 } catch { /* ignore */ }
 setLoading(false);
 }, [page, filterStatus, filterFn]);

 const loadStats = useCallback(async () => {
 try {
 const d = await fetch(`${API}/api/email/log/stats`, { credentials: 'include' }).then(r => r.json());
 setStats(d.stats);
 } catch { /* ignore */ }
 }, []);

 useEffect(() => { loadLog(); }, [loadLog]);
 useEffect(() => { loadStats(); }, [loadStats]);

 const handleResend = async (id) => {
 setResending(id);
 try {
 const res = await fetch(`${API}/api/email/log/${id}/resend`, { method: 'POST', credentials: 'include' });
 const d = await res.json();
 showToast(d.ok ? t('email.log.resendSuccess') : (d.error || t('common.error')));
 loadLog();
 loadStats();
 } catch { showToast(t('common.error')); }
 setResending(null);
 };

 const openDetail = async (entry) => {
 setDetailEntry(entry);
 setHistory(null);
 if (entry.email_queue_id) {
 setHistoryLoading(true);
 try {
 const r = await fetch(`${API}/api/email/log/queue/${entry.email_queue_id}/history`, { credentials: 'include' });
 const d = await r.json();
 if (d.ok) setHistory({ queue: d.queue, history: d.history });
 } catch { /* ignore */ }
 setHistoryLoading(false);
 }
 };

 const totalPages = Math.ceil(total / PAGE_SIZE);
 const successRate = stats && Number(stats.total) > 0
 ? ((Number(stats.gesendet) / Number(stats.total)) * 100).toFixed(1)
 : '–';

 return (
 <div className="space-y-4">
 {/* Stats bar */}
 {stats && (
 <div className="flex items-center gap-6 px-5 py-3 bg-surface-elevated rounded-card border border-line text-sm">
 <div><span className="text-[var(--text-secondary)]">{t('email.log.total')}:</span> <span className="font-semibold text-content">{stats.total}</span></div>
 <div><span className="text-[var(--text-secondary)]">{t('email.log.gesendet')}:</span> <span className="font-semibold text-green-500">{stats.gesendet}</span></div>
 <div><span className="text-[var(--text-secondary)]">{t('email.log.fehlerStatus')}:</span> <span className="font-semibold text-red-500">{stats.fehler}</span></div>
 <div><span className="text-[var(--text-secondary)]">{t('email.log.last24h')}:</span> <span className="font-semibold text-content">{stats.letzte_24h}</span></div>
 <div><span className="text-[var(--text-secondary)]">{t('email.stats.successRate')}:</span> <span className="font-semibold text-content">{successRate}%</span></div>
 </div>
 )}

 {/* Filters */}
 <div className="flex items-center gap-3">
 <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(0); }} className={`${inp} w-auto min-w-[160px]`}>
 <option value="">{t('email.filter.allStatus')}</option>
 <option value="gesendet">{t('email.log.gesendet')}</option>
 <option value="fehler">{t('email.log.fehlerStatus')}</option>
 </select>
 <select value={filterFn} onChange={e => { setFilterFn(e.target.value); setPage(0); }} className={`${inp} w-auto min-w-[160px]`}>
 <option value="">{t('email.filter.allFunctions')}</option>
 <option value="raw">raw</option>
 <option value="sla_eskalation">sla_eskalation</option>
 <option value="case_erinnerung">case_erinnerung</option>
 <option value="user_invitation">user_invitation</option>
 <option value="survey_monthly">survey_monthly</option>
 </select>
 <div className="flex-1" />
 <button onClick={() => { loadLog(); loadStats(); }} className="p-2 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] hover:text-content transition-colors">
 <RefreshCw className="w-4 h-4" />
 </button>
 </div>

 {/* Log table */}
 <div className="bg-surface-elevated rounded-card border border-line overflow-hidden">
 {loading ? (
 <div className="flex items-center justify-center h-48"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>
 ) : log.length === 0 ? (
 <div className="py-12 text-center text-sm text-[var(--text-tertiary)]">{t('email.log.empty')}</div>
 ) : (
 <table className="w-full text-sm">
 <thead>
 <tr className="border-b border-line">
 <th className="text-left px-5 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('email.log.datum')}</th>
 <th className="text-left px-5 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('email.log.empfaenger')}</th>
 <th className="text-left px-5 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('email.log.funktion')}</th>
 <th className="text-left px-5 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('email.log.status')}</th>
 <th className="text-left px-5 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('email.log.betreff')}</th>
 <th className="w-10" />
 </tr>
 </thead>
 <tbody className="divide-y divide-line/50">
 {log.map(entry => {
 const isQueueRow = !!entry.email_queue_id;
 const isLegacy = entry.row_type === 'legacy' || (!isQueueRow && entry.row_type !== 'queue');
 const queueSent = isQueueRow && entry.queue_status === 'sent';
 const queueFailed = isQueueRow && entry.queue_status === 'failed';
 const canResendQueue = isQueueRow && !queueSent && entry.queue_status !== 'cancelled';
 const canResendLegacy = isLegacy && entry.status === 'fehler' && entry.funktion !== 'raw';
 const showResend = canResendQueue || canResendLegacy;
 const attempts = entry.attempts || 1;
 const maxRetries = entry.max_retries || 0;
 const showAttempts = isQueueRow && attempts > 1;
 return (
 <tr key={entry.id} className={`hover:bg-surface-hover cursor-pointer transition-colors ${isLegacy ? 'opacity-70' : ''}`} onClick={() => openDetail(entry)}>
 <td className="px-5 py-2.5 text-xs text-[var(--text-secondary)] whitespace-nowrap">
 {entry.created_at ? new Date(entry.created_at).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–'}
 </td>
 <td className="px-5 py-2.5 text-content truncate max-w-[200px]">{entry.empfaenger_email}</td>
 <td className="px-5 py-2.5 text-xs text-[var(--text-secondary)] font-mono">{entry.funktion}</td>
 <td className="px-5 py-2.5">
 <div className="flex items-center gap-1.5 flex-wrap">
 <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
 entry.status === 'gesendet' ? 'bg-green-500/15 text-green-500' :
 queueFailed ? 'bg-red-500/15 text-red-500' :
 entry.status === 'fehler' && (entry.queue_status === 'pending' || entry.queue_status === 'held') ? 'bg-amber-500/15 text-amber-500' :
 'bg-red-500/15 text-red-500'
 }`}>
 {queueSent ? t('email.log.gesendet') :
 queueFailed ? t('email.log.fehlerStatus') :
 entry.queue_status === 'pending' || entry.queue_status === 'held' ? (t('email.log.queueRetrying') || 'Retry') :
 entry.status === 'gesendet' ? t('email.log.gesendet') : t('email.log.fehlerStatus')}
 </span>
 {showAttempts && (
 <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600" title={t('email.log.attemptsTooltip') || 'Versuche'}>
 {attempts}{maxRetries ? `/${maxRetries}` : ''}×
 </span>
 )}
 {isLegacy && (
 <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary)]" title="Legacy">
 L
 </span>
 )}
 </div>
 </td>
 <td className="px-5 py-2.5 text-content truncate max-w-[250px]">{entry.betreff}</td>
 <td className="px-3 py-2.5">
 {showResend && (
 <button onClick={e => { e.stopPropagation(); handleResend(entry.id); }} disabled={resending === entry.id}
 className="p-1.5 rounded-lg hover:bg-surface-hover text-primary-500 hover:text-primary-400 transition-colors disabled:opacity-50" title={t('email.log.resend')}>
 <RefreshCw className={`w-3.5 h-3.5 ${resending === entry.id ? 'animate-spin' : ''}`} />
 </button>
 )}
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 )}

 {/* Pagination */}
 {totalPages > 1 && (
 <div className="flex items-center justify-between px-5 py-3 border-t border-line">
 <span className="text-xs text-[var(--text-secondary)]">
 {t('email.pagination.showing', { from: page * PAGE_SIZE + 1, to: Math.min((page + 1) * PAGE_SIZE, total), total })}
 </span>
 <div className="flex items-center gap-1">
 <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
 className="p-1.5 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] disabled:opacity-30 transition-colors">
 <ChevronLeft className="w-4 h-4" />
 </button>
 <span className="text-xs text-content px-2">{page + 1} / {totalPages}</span>
 <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
 className="p-1.5 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] disabled:opacity-30 transition-colors">
 <ChevronRight className="w-4 h-4" />
 </button>
 </div>
 </div>
 )}
 </div>

 {/* Detail Modal */}
 <Modal open={!!detailEntry} onClose={() => { setDetailEntry(null); setHistory(null); }} title={t('email.log.detail')} size="lg"
 footer={detailEntry && (
 (detailEntry.email_queue_id && detailEntry.queue_status !== 'sent' && detailEntry.queue_status !== 'cancelled') ||
 (!detailEntry.email_queue_id && detailEntry.status === 'fehler' && detailEntry.funktion !== 'raw')
 ) ? (
 <button onClick={() => { handleResend(detailEntry.id); setDetailEntry(null); }}
 className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors">
 <RefreshCw className="w-3.5 h-3.5" /> {t('email.log.resend')}
 </button>
 ) : null}>
 {detailEntry && (
 <div className="space-y-3">
 <div className="grid grid-cols-2 gap-3 text-sm">
 <div><span className="text-[var(--text-secondary)]">{t('email.log.empfaenger')}:</span> <span className="text-content ml-1">{detailEntry.empfaenger_email}</span></div>
 <div><span className="text-[var(--text-secondary)]">{t('email.log.funktion')}:</span> <span className="text-content ml-1 font-mono">{detailEntry.funktion}</span></div>
 <div><span className="text-[var(--text-secondary)]">{t('email.log.status')}:</span>
 <span className={`ml-1 text-[10px] font-semibold px-2 py-0.5 rounded ${detailEntry.status === 'gesendet' ? 'bg-green-500/15 text-green-500' : 'bg-red-500/15 text-red-500'}`}>
 {detailEntry.status}
 </span>
 </div>
 <div><span className="text-[var(--text-secondary)]">{t('email.log.datum')}:</span> <span className="text-content ml-1">{detailEntry.created_at ? new Date(detailEntry.created_at).toLocaleString('de-AT') : '–'}</span></div>
 </div>
 <div className="text-sm"><span className="text-[var(--text-secondary)]">{t('email.log.betreff')}:</span> <span className="text-content ml-1 font-medium">{detailEntry.betreff}</span></div>

 {/* #941 Queue-Info & Audit-History */}
 {detailEntry.email_queue_id && (
 <div className="px-3 py-2.5 rounded-lg border border-line bg-[var(--bg-inset,#F8F9FC)] space-y-2">
 <div className="flex items-center gap-3 text-xs flex-wrap">
 <span className="text-[var(--text-secondary)]">Queue-ID: <span className="font-mono text-content">#{detailEntry.email_queue_id}</span></span>
 {detailEntry.queue_status && (
 <span className="text-[var(--text-secondary)]">Status: <span className="font-semibold text-content">{detailEntry.queue_status}</span></span>
 )}
 {(detailEntry.retry_count !== undefined && detailEntry.max_retries) && (
 <span className="text-[var(--text-secondary)]">Versuche: <span className="font-semibold text-content">{detailEntry.retry_count}/{detailEntry.max_retries}</span></span>
 )}
 {detailEntry.next_retry_at && detailEntry.queue_status === 'pending' && (
 <span className="text-[var(--text-secondary)]">Nächster Versuch: <span className="text-content">{new Date(detailEntry.next_retry_at).toLocaleString('de-AT')}</span></span>
 )}
 </div>
 {historyLoading && <div className="text-xs text-[var(--text-tertiary)]">{t('common.loading') || 'Lädt...'}</div>}
 {history && history.history && history.history.length > 0 && (
 <div className="space-y-1">
 <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-tertiary)] mt-1">Audit-History</div>
 <div className="space-y-1 max-h-[200px] overflow-auto">
 {history.history.map((h, i) => (
 <div key={h.id} className="flex items-center gap-2 text-xs px-2 py-1.5 rounded bg-surface-card border border-line">
 <span className="text-[var(--text-tertiary)] tabular-nums">#{i + 1}</span>
 <span className="text-[var(--text-secondary)] tabular-nums whitespace-nowrap">{new Date(h.created_at).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
 <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${h.status === 'gesendet' ? 'bg-green-500/15 text-green-500' : h.status === 'fehler' ? 'bg-red-500/15 text-red-500' : 'bg-[var(--bg-inset)] text-[var(--text-tertiary)]'}`}>
 {h.status}
 </span>
 {h.error_message && <span className="text-[var(--text-tertiary)] truncate flex-1" title={h.error_message}>{h.error_message}</span>}
 </div>
 ))}
 </div>
 </div>
 )}
 </div>
 )}

 {detailEntry.error_message && (
 <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">
 {t('email.log.fehler')}: {detailEntry.error_message}
 </div>
 )}
 {detailEntry.referenz_typ && (
 <div className="text-xs text-[var(--text-secondary)]">
 Referenz: {detailEntry.referenz_typ} {detailEntry.referenz_id ? `#${detailEntry.referenz_id}` : ''}
 </div>
 )}
 {detailEntry.body_html && (
 <div>
 <div className="text-xs font-medium text-[var(--text-secondary)] mb-1">{t('email.log.renderedContent')}</div>
 <div className="bg-[#0a0a0f] rounded-lg p-4 overflow-auto max-h-[300px]" dangerouslySetInnerHTML={{ __html: detailEntry.body_html }} />
 </div>
 )}
 </div>
 )}
 </Modal>
 </div>
 );
}

/* ═══════════════════════════════════════════════════════════
 SendConfigCard — 3-Stufen: Aktiv / Test-Modus / Deaktiviert
 ═══════════════════════════════════════════════════════════ */
const MODE_DEFS = [
 { key: 'active', dotColor: 'bg-green-500', labelKey: 'email.modeActive' },
 { key: 'test', dotColor: 'bg-amber-500', labelKey: 'email.modeTest' },
 { key: 'disabled', dotColor: 'bg-red-500', labelKey: 'email.modeDisabled' },
];

function SendConfigCard({ t, showToast }) {
 const [mode, setMode] = useState('active');
 const [testAddress, setTestAddress] = useState('');
 const [savedMode, setSavedMode] = useState('active');
 const [savedAddress, setSavedAddress] = useState('');
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [showConfirm, setShowConfirm] = useState(false);
 const [pendingMode, setPendingMode] = useState(null);

 useEffect(() => {
 fetch(`${API}/api/email/send-config`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => {
 if (d.config) {
 setMode(d.config.mode || 'active');
 setTestAddress(d.config.test_address || '');
 setSavedMode(d.config.mode || 'active');
 setSavedAddress(d.config.test_address || '');
 }
 })
 .catch(() => {})
 .finally(() => setLoading(false));
 }, []);

 const isDirty = mode !== savedMode || testAddress !== savedAddress;
 const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
 const canSave = isDirty && !(mode === 'test' && testAddress && !isValidEmail(testAddress));

 const handleModeSelect = (newMode) => {
 if (newMode === 'disabled' && mode !== 'disabled') {
 setPendingMode('disabled');
 setShowConfirm(true);
 } else {
 setMode(newMode);
 }
 };

 const confirmDisable = () => {
 setMode('disabled');
 setPendingMode(null);
 setShowConfirm(false);
 };

 const cancelDisable = () => {
 setPendingMode(null);
 setShowConfirm(false);
 };

 const handleSave = async () => {
 setSaving(true);
 try {
 const res = await fetch(`${API}/api/email/send-config`, {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ mode, test_address: testAddress }),
 });
 if (res.ok) {
 setSavedMode(mode);
 setSavedAddress(testAddress);
 showToast(t('email.savedSuccess'));
 } else showToast(t('common.error'));
 } catch { showToast(t('common.error')); }
 setSaving(false);
 };

 if (loading) return <div className="flex items-center justify-center h-24"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>;

 return (
 <>
 <div className="bg-surface-elevated rounded-card border border-line mb-4">
 <div className="px-5 py-3 border-b border-line">
 <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
 <Mail className="w-4 h-4" /> {t('email.sendConfig')}
 </h2>
 </div>
 <div className="px-5 py-4 space-y-4">
 {/* 3-Stufen Segment-Toggle */}
 <div className="inline-flex gap-0.5 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-line">
 {MODE_DEFS.map(md => (
 <button
 key={md.key}
 onClick={() => handleModeSelect(md.key)}
 className={`flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-medium transition-all ${
 mode === md.key
 ? 'bg-white dark:bg-surface-elevated text-content font-semibold shadow-sm'
 : 'text-[var(--text-secondary)] hover:text-content'
 }`}
 >
 <span className={`inline-block w-2 h-2 rounded-full ${md.dotColor}`} />
 {t(md.labelKey)}
 </button>
 ))}
 </div>

 {/* Mode-specific info */}
 {mode === 'active' && (
 <p className="text-xs text-[var(--text-secondary)]">{t('email.modeActiveDesc')}</p>
 )}
 {mode === 'test' && (
 <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 text-xs">
 <TestTube2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
 <span>{t('email.modeTestDesc')}</span>
 </div>
 )}
 {mode === 'disabled' && (
 <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-xs">
 <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
 <span>{t('email.modeDisabledDesc')}</span>
 </div>
 )}

 {/* Test-Adresse (nur bei Test-Modus) */}
 {mode === 'test' && (
 <label className="block">
 <span className="text-xs font-medium text-[var(--text-secondary)]">{t('email.testAddress')}</span>
 <input
 value={testAddress}
 onChange={e => setTestAddress(e.target.value)}
 className={`mt-1 ${inp}`}
 placeholder={t('email.testAddressPlaceholder')}
 type="email"
 />
 {testAddress && !isValidEmail(testAddress) && (
 <span className="text-xs text-red-500 mt-1 block">Invalid email address</span>
 )}
 </label>
 )}

 <div className="flex justify-end pt-1">
 <button
 onClick={handleSave}
 disabled={saving || !canSave}
 className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50"
 >
 {saving ? '...' : t('common.save')}
 </button>
 </div>
 </div>
 </div>

 {/* Bestätigungs-Dialog: nur bei Wechsel auf "Deaktiviert" */}
 {showConfirm && (
 <Modal open={showConfirm} onClose={cancelDisable} title={t('email.confirmDisableTitle')}>
 <div className="p-5 space-y-4">
 <p className="text-sm text-content">{t('email.confirmDisableText')}</p>
 <div className="flex justify-end gap-2">
 <button onClick={cancelDisable}
 className="px-4 py-2 rounded-lg text-sm font-medium border border-line hover:bg-surface-hover transition-colors text-content">
 {t('common.cancel')}
 </button>
 <button onClick={confirmDisable}
 className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors">
 {t('email.confirmDisableBtn')}
 </button>
 </div>
 </div>
 </Modal>
 )}
 </>
 );
}

/* ═══════════════════════════════════════════════════════════
 Tab 3: SMTP Configuration
 ═══════════════════════════════════════════════════════════ */
function SmtpTab({ t, showToast }) {
 const [smtp, setSmtp] = useState({ host: '', port: 587, user: '', pass: '', from_name: '', from_address: '' });
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [testing, setTesting] = useState(false);
 const [testResult, setTestResult] = useState(null);
 const [showPass, setShowPass] = useState(false);

 useEffect(() => {
 fetch(`${API}/api/email/smtp`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => { if (d.smtp) setSmtp(d.smtp); })
 .catch(() => {})
 .finally(() => setLoading(false));
 }, []);

 const handleSave = async () => {
 setSaving(true);
 try {
 const res = await fetch(`${API}/api/email/smtp`, {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify(smtp),
 });
 if (res.ok) showToast(t('email.smtp.saved'));
 else showToast(t('common.error'));
 } catch { showToast(t('common.error')); }
 setSaving(false);
 };

 const handleTest = async () => {
 setTesting(true);
 setTestResult(null);
 try {
 const res = await fetch(`${API}/api/email/smtp/test`, { method: 'POST', credentials: 'include' });
 const d = await res.json();
 setTestResult(d);
 showToast(d.ok ? t('email.smtp.testSuccess') : (d.error || t('email.smtp.testFailed')));
 } catch { showToast(t('email.smtp.testFailed')); }
 setTesting(false);
 };

 if (loading) return <div className="flex items-center justify-center h-48"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>;

 return (
 <div className="space-y-4">
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line">
 <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
 <Server className="w-4 h-4" /> {t('email.smtp.title')}
 </h2>
 </div>
 <div className="px-5 py-4 space-y-4">
 <div className="grid grid-cols-2 gap-3">
 <label className="block">
 <span className="text-xs font-medium text-[var(--text-secondary)]">{t('email.smtp.host')}</span>
 <input value={smtp.host} onChange={e => setSmtp(p => ({ ...p, host: e.target.value }))} className={`mt-1 ${inp}`} placeholder="smtp.example.com" />
 </label>
 <label className="block">
 <span className="text-xs font-medium text-[var(--text-secondary)]">{t('email.smtp.port')}</span>
 <input type="number" value={smtp.port} onChange={e => setSmtp(p => ({ ...p, port: Number(e.target.value) }))} className={`mt-1 ${inp}`} />
 </label>
 </div>
 <div className="grid grid-cols-2 gap-3">
 <label className="block">
 <span className="text-xs font-medium text-[var(--text-secondary)]">{t('email.smtp.user')}</span>
 <input value={smtp.user} onChange={e => setSmtp(p => ({ ...p, user: e.target.value }))} className={`mt-1 ${inp}`} placeholder="noreply@weship.eu" />
 </label>
 <label className="block relative">
 <span className="text-xs font-medium text-[var(--text-secondary)]">{t('email.smtp.pass')}</span>
 <div className="relative mt-1">
 <input type={showPass ? 'text' : 'password'} value={smtp.pass} onChange={e => setSmtp(p => ({ ...p, pass: e.target.value }))} className={inp} />
 <button type="button" onClick={() => setShowPass(!showPass)}
 className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] hover:text-content transition-colors">
 <Eye className="w-4 h-4" />
 </button>
 </div>
 </label>
 </div>
 <div className="grid grid-cols-2 gap-3">
 <label className="block">
 <span className="text-xs font-medium text-[var(--text-secondary)]">{t('email.smtp.fromName')}</span>
 <input value={smtp.from_name} onChange={e => setSmtp(p => ({ ...p, from_name: e.target.value }))} className={`mt-1 ${inp}`} placeholder="WeShip" />
 </label>
 <label className="block">
 <span className="text-xs font-medium text-[var(--text-secondary)]">{t('email.smtp.fromAddress')}</span>
 <input value={smtp.from_address} onChange={e => setSmtp(p => ({ ...p, from_address: e.target.value }))} className={`mt-1 ${inp}`} placeholder="noreply@weship.eu" />
 </label>
 </div>

 {/* Test result */}
 {testResult && (
 <div className={`px-3 py-2 rounded-lg text-xs ${testResult.ok ? 'bg-green-500/10 border border-green-500/20 text-green-500' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
 {testResult.ok ? t('email.smtp.testSuccess') : testResult.error}
 </div>
 )}

 <div className="flex justify-end gap-2 pt-2">
 <button onClick={handleTest} disabled={testing || !smtp.host}
 className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-line hover:bg-surface-hover transition-colors text-content disabled:opacity-50">
 <RefreshCw className={`w-3.5 h-3.5 ${testing ? 'animate-spin' : ''}`} /> {testing ? '...' : t('email.smtp.test')}
 </button>
 <button onClick={handleSave} disabled={saving || !smtp.host}
 className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
 {saving ? '...' : t('email.smtp.save')}
 </button>
 </div>
 </div>
 </div>
 </div>
 );
}

/* ═══════════════════════════════════════════════════════════
 Tab 4: Test
 ═══════════════════════════════════════════════════════════ */
function TestTab({ t, showToast }) {
 const { user } = useAuth();
 const [templates, setTemplates] = useState([]);
 const [selectedId, setSelectedId] = useState('');
 const [to, setTo] = useState(user?.email || '');
 const [vars, setVars] = useState({ ...TEST_DATA });
 const [preview, setPreview] = useState(null);
 const [sending, setSending] = useState(false);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 fetch(`${API}/api/email/templates`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => setTemplates((d.templates || []).filter(t => t.aktiv)))
 .catch(() => {})
 .finally(() => setLoading(false));
 }, []);

 const selectedTpl = templates.find(t => t.id === Number(selectedId));

 const handlePreview = async () => {
 if (!selectedId) return;
 try {
 const res = await fetch(`${API}/api/email/templates/${selectedId}/preview`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ variablen: vars }),
 });
 const d = await res.json();
 if (d.ok) setPreview(d);
 } catch { showToast(t('common.error')); }
 };

 const handleSend = async () => {
 if (!selectedId || !to) return;
 setSending(true);
 try {
 const res = await fetch(`${API}/api/email/templates/${selectedId}/test`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ to, variablen: vars }),
 });
 const d = await res.json();
 showToast(d.ok ? t('email.templates.testSent') : (d.reason || t('email.templates.testFailed')));
 } catch { showToast(t('email.templates.testFailed')); }
 setSending(false);
 };

 // Extract variables from selected template
 const extractVarsFromTemplate = (tpl) => {
 if (!tpl) return [];
 const text = (tpl.betreff || '') + (tpl.body_html || '');
 const matches = text.match(/\{\{(\w+(?:\.\w+)*)\}\}/g);
 if (!matches) return [];
 return [...new Set(matches.map(m => m.slice(2, -2)))];
 };

 const templateVars = extractVarsFromTemplate(selectedTpl);

 if (loading) return <div className="flex items-center justify-center h-48"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>;

 return (
 <div className="space-y-4">
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line">
 <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
 <FlaskConical className="w-4 h-4" /> {t('email.test.title')}
 </h2>
 </div>
 <div className="px-5 py-4 space-y-4">
 <div className="grid grid-cols-2 gap-3">
 <label className="block">
 <span className="text-xs font-medium text-[var(--text-secondary)]">{t('email.test.template')}</span>
 <select value={selectedId} onChange={e => { setSelectedId(e.target.value); setPreview(null); }} className={`mt-1 ${inp}`}>
 <option value="">—</option>
 {templates.map(tpl => <option key={tpl.id} value={tpl.id}>{tpl.name} ({tpl.funktion})</option>)}
 </select>
 </label>
 <label className="block">
 <span className="text-xs font-medium text-[var(--text-secondary)]">{t('email.test.to')}</span>
 <input value={to} onChange={e => setTo(e.target.value)} className={`mt-1 ${inp}`} placeholder="email@example.com" />
 </label>
 </div>

 {/* Variable editor */}
 {templateVars.length > 0 && (
 <div>
 <span className="text-xs font-medium text-[var(--text-secondary)]">{t('email.editor.variables')}</span>
 <div className="mt-1.5 space-y-2">
 {templateVars.map(v => (
 <div key={v} className="flex items-center gap-2">
 <span className="text-xs font-mono text-[var(--text-secondary)] w-48 truncate">{`{{${v}}}`}</span>
 <input value={vars[v] || ''} onChange={e => setVars(p => ({ ...p, [v]: e.target.value }))} className={`flex-1 ${inp} text-xs`} />
 </div>
 ))}
 </div>
 </div>
 )}

 <div className="flex gap-2">
 <button onClick={handlePreview} disabled={!selectedId}
 className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border border-line hover:bg-surface-hover transition-colors text-content disabled:opacity-50">
 <Eye className="w-3.5 h-3.5" /> {t('email.test.showPreview')}
 </button>
 <button onClick={handleSend} disabled={sending || !selectedId || !to}
 className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
 <Send className="w-3.5 h-3.5" /> {sending ? '...' : t('email.test.send')}
 </button>
 </div>
 </div>
 </div>

 {/* Preview */}
 {preview && (
 <div className="bg-surface-elevated rounded-card border border-line">
 <div className="px-5 py-3 border-b border-line flex items-center justify-between">
 <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
 <Eye className="w-4 h-4" /> {t('email.editor.preview')}
 </h2>
 <button onClick={() => setPreview(null)} className="p-1 rounded-lg hover:bg-surface-hover text-[var(--text-tertiary)] transition-colors">
 <X className="w-4 h-4" />
 </button>
 </div>
 <div className="p-5">
 <div className="text-xs text-[var(--text-secondary)] mb-2">{t('email.templates.betreff')}: <span className="text-content font-medium">{preview.betreff}</span></div>
 <div className="bg-[#0a0a0f] rounded-lg p-4 overflow-auto max-h-[400px]" dangerouslySetInnerHTML={{ __html: preview.html }} />
 </div>
 </div>
 )}
 </div>
 );
}

/* ═══════════════════════════════════════════════════════════
 InboundTab — Eingehende E-Mails Übersicht
 ═══════════════════════════════════════════════════════════ */

function InboundTab({ t, showToast }) {
 const [emails, setEmails] = useState([]);
 const [total, setTotal] = useState(0);
 const [page, setPage] = useState(1);
 const [loading, setLoading] = useState(true);
 const [statusFilter, setStatusFilter] = useState('');
 const [search, setSearch] = useState('');
 const [selected, setSelected] = useState(null);
 const [detailData, setDetailData] = useState(null);

 const STATUS_CLS = {
 processed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
 pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
 failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
 ignored: 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)] dark:bg-[var(--bg-inset,#F8F9FC)] dark:text-[var(--text-tertiary,#6B7280)]',
 };

 const load = useCallback(async () => {
 setLoading(true);
 try {
 const params = new URLSearchParams({ page: String(page), limit: '25' });
 if (statusFilter) params.set('status', statusFilter);
 if (search) params.set('search', search);
 const res = await fetch(`${API}/api/inbound-emails?${params}`, { credentials: 'include' });
 const data = await res.json();
 if (data.ok) {
 setEmails(data.emails || []);
 setTotal(data.total || 0);
 }
 } catch { /* ignore */ }
 setLoading(false);
 }, [page, statusFilter, search]);

 useEffect(() => { load(); }, [load]);

 const handleReprocess = async (id) => {
 try {
 const res = await fetch(`${API}/api/inbound-emails/${id}/reprocess`, {
 method: 'POST', credentials: 'include',
 });
 const data = await res.json();
 if (data.ok) { showToast(`${t('inboundEmail.reprocess')}: OK`); load(); }
 else showToast(data.error || 'Error');
 } catch (err) { showToast(err.message); }
 };

 const handleDelete = async (id) => {
 if (!confirm('Delete?')) return;
 try {
 await fetch(`${API}/api/inbound-emails/${id}`, { method: 'DELETE', credentials: 'include' });
 showToast('Deleted');
 load();
 } catch (err) { showToast(err.message); }
 };

 const showDetail = async (id) => {
 try {
 const res = await fetch(`${API}/api/inbound-emails/${id}`, { credentials: 'include' });
 const data = await res.json();
 if (data.ok) {
 setDetailData(data);
 setSelected(id);
 }
 } catch { /* ignore */ }
 };

 const pages = Math.ceil(total / 25);

 return (
 <div className="space-y-4">
 {/* Filter bar */}
 <div className="flex flex-wrap gap-3 items-center">
 <select
 className="px-3 py-2 text-sm rounded-lg border border-line bg-surface-elevated text-content"
 value={statusFilter}
 onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
 >
 <option value="">All Status</option>
 <option value="processed">{t('inboundEmail.processed')}</option>
 <option value="pending">Pending</option>
 <option value="failed">Failed</option>
 <option value="ignored">Ignored</option>
 </select>
 <div className="relative flex-1 min-w-[200px]">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]" />
 <input
 className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30"
 placeholder={`${t('common.search') || 'Search'}...`}
 value={search}
 onChange={e => setSearch(e.target.value)}
 onKeyDown={e => e.key === 'Enter' && load()}
 />
 </div>
 <button onClick={load} className="p-2 rounded-lg border border-line text-[var(--text-secondary)] hover:text-content hover:bg-[var(--bg-card-hover)]">
 <RefreshCw className="w-4 h-4" />
 </button>
 </div>

 {/* Table */}
 <div className="border border-line rounded-card bg-surface-elevated overflow-x-auto">
 <table className="w-full text-sm">
 <thead>
 <tr className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] border-b border-line">
 <th className="text-left py-2.5 px-3 font-semibold">{t('common.date') || 'Date'}</th>
 <th className="text-left py-2.5 px-3 font-semibold">{t('common.from') || 'From'}</th>
 <th className="text-left py-2.5 px-3 font-semibold">{t('common.subject') || 'Subject'}</th>
 <th className="text-left py-2.5 px-3 font-semibold">{t('inboundEmail.source') || 'Customer'}</th>
 <th className="text-left py-2.5 px-3 font-semibold">Case</th>
 <th className="text-left py-2.5 px-3 font-semibold">Status</th>
 <th className="text-right py-2.5 px-3 font-semibold"></th>
 </tr>
 </thead>
 <tbody>
 {loading ? (
 <tr><td colSpan={7} className="py-8 text-center text-[var(--text-secondary)]">Loading...</td></tr>
 ) : emails.length === 0 ? (
 <tr><td colSpan={7} className="py-8 text-center text-[var(--text-secondary)]">No emails</td></tr>
 ) : emails.map(em => (
 <tr key={em.id} className="border-b border-line/50 hover:bg-[var(--bg-card-hover)] cursor-pointer" onClick={() => showDetail(em.id)}>
 <td className="py-2.5 px-3 text-[var(--text-tertiary)] whitespace-nowrap text-xs">
 {new Date(em.received_at).toLocaleString('de', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
 </td>
 <td className="py-2.5 px-3 text-content truncate max-w-[160px]">
 <div className="flex items-center gap-1.5">
 {em.has_attachments && <Paperclip className="w-3 h-3 text-[var(--text-tertiary)] flex-shrink-0" />}
 {em.from_name || em.from_address}
 </div>
 </td>
 <td className="py-2.5 px-3 text-content truncate max-w-[220px]">{em.subject || '–'}</td>
 <td className="py-2.5 px-3 text-[var(--text-secondary)] truncate max-w-[120px]">{em.kunde_name || <span className="text-[var(--text-tertiary)] italic">{t('inboundEmail.noCustomerMatch')}</span>}</td>
 <td className="py-2.5 px-3">
 {em.case_id ? (
 <span className="text-primary-600 dark:text-primary-400 font-medium text-xs">#{em.case_id}</span>
 ) : '–'}
 </td>
 <td className="py-2.5 px-3">
 <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_CLS[em.processing_status] || STATUS_CLS.pending}`}>
 {em.processing_status}
 </span>
 </td>
 <td className="py-2.5 px-3 text-right">
 <div className="flex items-center gap-1 justify-end" onClick={e => e.stopPropagation()}>
 {(em.processing_status === 'failed' || em.processing_status === 'ignored') && (
 <button onClick={() => handleReprocess(em.id)} className="p-1 rounded text-[var(--text-secondary)] hover:text-primary-600" title={t('inboundEmail.reprocess')}>
 <RotateCcw className="w-3.5 h-3.5" />
 </button>
 )}
 <button onClick={() => handleDelete(em.id)} className="p-1 rounded text-[var(--text-secondary)] hover:text-red-500" title="Delete">
 <Trash2 className="w-3.5 h-3.5" />
 </button>
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>

 {/* Pagination */}
 {pages > 1 && (
 <div className="flex items-center justify-between text-xs text-[var(--text-secondary)]">
 <span>{total} {t('common.entries') || 'entries'}</span>
 <div className="flex gap-1">
 <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2 py-1 rounded border border-line disabled:opacity-30">
 <ChevronLeft className="w-3.5 h-3.5" />
 </button>
 <span className="px-2 py-1">{page}/{pages}</span>
 <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="px-2 py-1 rounded border border-line disabled:opacity-30">
 <ChevronRight className="w-3.5 h-3.5" />
 </button>
 </div>
 </div>
 )}

 {/* Detail Dialog */}
 {selected && detailData && (
 <Modal open onClose={() => { setSelected(null); setDetailData(null); }} title={detailData.email?.subject || 'Email Detail'}>
 <div className="space-y-4 text-sm">
 <div className="grid grid-cols-2 gap-3">
 <div>
 <span className="text-[var(--text-secondary)] text-xs">{t('common.from') || 'From'}</span>
 <p className="text-content">{detailData.email.from_name} &lt;{detailData.email.from_address}&gt;</p>
 </div>
 <div>
 <span className="text-[var(--text-secondary)] text-xs">{t('common.date') || 'Date'}</span>
 <p className="text-content">{new Date(detailData.email.received_at).toLocaleString('de')}</p>
 </div>
 <div>
 <span className="text-[var(--text-secondary)] text-xs">Status</span>
 <p><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_CLS[detailData.email.processing_status]}`}>
 {detailData.email.processing_status}
 </span></p>
 </div>
 <div>
 <span className="text-[var(--text-secondary)] text-xs">Case</span>
 <p className="text-content">{detailData.email.case_id ? `#${detailData.email.case_id} — ${detailData.email.case_title || ''}` : '–'}</p>
 </div>
 </div>

 {detailData.email.error_message && (
 <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-xs">
 <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
 {detailData.email.error_message}
 </div>
 )}

 <div>
 <span className="text-[var(--text-secondary)] text-xs block mb-1">Body</span>
 <pre className="text-content bg-surface-inset p-3 rounded-lg text-xs whitespace-pre-wrap max-h-[300px] overflow-y-auto border border-line">
 {detailData.email.body_text || '(empty)'}
 </pre>
 </div>

 {detailData.attachments?.length > 0 && (
 <div>
 <span className="text-[var(--text-secondary)] text-xs block mb-1">
 {t('inboundEmail.attachments')} ({detailData.attachments.length})
 </span>
 <div className="space-y-1">
 {detailData.attachments.map(att => (
 <div key={att.id} className="flex items-center gap-2 text-xs text-content bg-surface-inset rounded px-2 py-1.5 border border-line">
 <Paperclip className="w-3 h-3 text-[var(--text-tertiary)]" />
 <span>{att.filename}</span>
 <span className="text-[var(--text-tertiary)]">({(att.size_bytes / 1024).toFixed(1)} KB)</span>
 </div>
 ))}
 </div>
 </div>
 )}
 </div>
 </Modal>
 )}
 </div>
 );
}

/* ═══════════════════════════════════════════════════════════
 Main Component
 ═══════════════════════════════════════════════════════════ */
export default function EmailVerwaltung() {
 const { t, language } = useLanguage();
 const [activeTab, setActiveTab] = useTabParam('templates');
 const [toast, setToast] = useState(null);

 const showToast = (msg) => setToast({ message: msg });

 const TABS = TAB_DEFS.map(td => ({ ...td, label: t(td.labelKey) }));

 return (
 <div className="px-6 lg:px-8 py-6">
 {/* Header */}
 <div className="flex items-center gap-3 mb-1">
 <Mail className="w-6 h-6 text-[#338edc]" />
 <h1 className="text-xl font-bold text-content">{t('email.title')}</h1>
 </div>
 <p className="text-sm text-[var(--text-secondary)] mb-6">{t('email.subtitle')}</p>

 {/* Tabs */}
 <div className="flex gap-1 mb-6 bg-surface-inset border border-line rounded-card p-1">
 {TABS.map(tab => (
 <button
 key={tab.key}
 onClick={() => setActiveTab(tab.key)}
 className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${
 activeTab === tab.key
 ? 'bg-white dark:bg-surface-elevated text-content shadow-sm'
 : 'text-[var(--text-secondary)] hover:text-content'
 }`}
 >
 <tab.Icon className="w-3.5 h-3.5" />
 {tab.label}
 </button>
 ))}
 </div>

 {/* Tab content */}
 {activeTab === 'templates' && <TemplatesTab t={t} showToast={showToast} />}
 {activeTab === 'queue' && <QueueTab t={t} showToast={showToast} />}
 {activeTab === 'categories' && <CategoriesTab t={t} language={language} showToast={showToast} />}
 {activeTab === 'log' && <LogTab t={t} showToast={showToast} />}
 {activeTab === 'inbound' && <InboundTab t={t} showToast={showToast} />}
 {activeTab === 'smtp' && (
 <>
 <SendConfigCard t={t} showToast={showToast} />
 <SmtpTab t={t} showToast={showToast} />
 </>
 )}
 {activeTab === 'test' && <TestTab t={t} showToast={showToast} />}

 {toast && <Toast message={toast.message} onClose={() => setToast(null)} />}
 </div>
 );
}
